import React, { useState, useEffect, useRef } from 'react';
import {
  Save, ArrowLeft, MapPin, Upload, X, Plus, Loader,
  Image as ImageIcon, Calendar, Star, Eye, Globe, FileText, GripVertical,
  ChevronLeft, ChevronRight, ZoomIn
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { uploadFile } from '../lib/storage';
import { cmsFetch } from '../lib/cmsApi';
import CMSAuth from '../components/CMSAuth';
import HighlightsManager from '../components/HighlightsManager';
import WaypointEditor from '../components/WaypointEditor';
import { TAG_GROUPS } from '../utils/tagLabels';
import tagLabel from '../utils/tagLabels';

const MAPS_LIBRARIES = ['places'];

const THEME_SONG_SYSTEM_PROMPT = "Generate 3 theme songs for this walk. " +
  "STRICT: You are FORBIDDEN from choosing any song that references the city, state, or region in its title or lyrics. " +
  "'California Gurls', 'New York New York', 'LA Woman', 'Empire State of Mind', 'City of Angels', 'Going to California', 'Hotel California', 'California Dreamin' and any similar geographically obvious choices are BANNED. " +
  "Each song must connect to the specific mood, history, or cultural character of this particular walk, not its location. " +
  "Prefer unexpected but defensible choices from any genre or era. Don't default to mainstream pop. " +
  "Consider: the mood of the walk, the time of day, the cultural history of the neighborhood, the kind of person who would love this walk. " +
  "Suggest real songs with real artists. Vary the genres across all 3 suggestions. " +
  "Format: Song Title - Artist Name only, one per line, no explanation, no numbering, no extra text.";

const MOVIE_SYSTEM_PROMPT = "You are a creative curator for Wanderloop, an urban walking app. Given a walk's details, suggest 3 movies that match the visual mood, setting, or feeling of the route. " +
  "Rules: suggest real films with the year in parentheses. At least one should be set in or near the walk's actual city. Vary the era. " +
  "Think cinematically: what movie's color palette, pacing, and tone matches walking this route? " +
  "Format: Movie Title (Year) | one-sentence explanation of why this film captures the walk's spirit. One per line, no numbering.";

const META_DESCRIPTION_SYSTEM_PROMPT = "You are an SEO specialist for Wanderloop, an urban walking discovery platform. Write a single meta description for a walk page that will rank well in Google and compel someone to click. Rules: exactly 150-160 characters including spaces. Must include the neighborhood or city name naturally. Must hint at 1-2 specific stops or highlights from the route. Should feel inviting and active, not robotic. No hashtags, no ALL CAPS, no ellipsis at the end. Do not start with Discover or Explore — be more specific. Return only the meta description text, nothing else.";

const CMSWalkForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    practical_summary: '',
    starting_point: '',
    ending_point: '',
    starting_address: '',
    ending_address: '',
    starting_coords: { lat: '', lng: '' },
    ending_coords: { lat: '', lng: '' },
    route_coordinates: [],
    distance_miles: '',
    duration_minutes: '',
    difficulty: 'Easy',
    walk_shape: 'loop',
    safety_level: 'Safe Anytime',
    categories: [],
    region: '',
    country: 'us',
    city: 'los-angeles',
    neighborhood: '',
    seo_tags: [],
    tags: [],
    walk_type_tags: [],
    highlights: [''],
    cultural_pois: [],
    _unsplash_attributions: [],
    waypoints: [],
    related_gear_ids: [],
    accessibility: {
      wheelchair_accessible: false,
      stroller_friendly: false,
      dog_friendly: false,
      parking_available: false,
      restrooms: false,
      shade_coverage: 'Limited'
    },
    images: [],
    main_image: '',
    walk_type: 'curated',
    visibility: 'public',
    featured: false,
    status: 'published',
    best_times: [],
    gear_notes: '',
    fun_categories: {
      theme_song: '',
      vibe_in_3_words: '',
      instagram_moment: '',
      perfect_snack: '',
      walking_challenge: '',
      movie_that_matches: '',
    },
    meta_title: '',
    meta_description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  const [walkFeedback, setWalkFeedback] = useState([]);
  const [selectedHighlights, setSelectedHighlights] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [relatedGear, setRelatedGear] = useState([]); // [{slug, name, category, brand, price}]
  const [gearSearch, setGearSearch] = useState('');
  const [gearResults, setGearResults] = useState([]); // results for current search query
  const [gearSearching, setGearSearching] = useState(false);
  const [gearDropdownOpen, setGearDropdownOpen] = useState(false);
  const gearBlurTimeout = useRef(null);

  // Debounced gear search — fires after 250ms when query has >= 2 chars.
  // Server-side filter on name + brand; replaces the prior fetch-all-on-focus pattern.
  useEffect(() => {
    const q = gearSearch.trim();
    if (q.length < 2) { setGearResults([]); setGearSearching(false); return; }
    let cancelled = false;
    setGearSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gear?search=${encodeURIComponent(q)}`);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setGearResults(data.items || []);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setGearSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [gearSearch]);
  const [aiSuggestions, setAiSuggestions] = useState({}); // { theme_song: [...], movie_that_matches: [...] }
  const [aiLoading, setAiLoading] = useState({});          // { theme_song: true, ... }
  const [walkConcept, setWalkConcept] = useState('');
  const [masterGenerating, setMasterGenerating] = useState(false);
  const [masterProgress, setMasterProgress] = useState(null); // { current, completed:[], failed:[], total }
  const masterAbort = useRef(false);
  // Stores the original URL→attribution mapping loaded from DB, immune to form state corruption
  const originalAttrMap = useRef(new Map());
  const [routeWarnings, setRouteWarnings] = useState([]); // [{from, to, distance}]
  const [imagePreviewIdx, setImagePreviewIdx] = useState(null); // null = closed, number = index into allImages
  const [difficultyAutoAssigned, setDifficultyAutoAssigned] = useState(false);
  const [customNeighborhood, setCustomNeighborhood] = useState(false);

  // Distance-based difficulty and duration helpers
  const difficultyFromMiles = (miles) => {
    if (miles < 3) return 'Easy';
    if (miles <= 5) return 'Moderate';
    return 'Hard';
  };
  const durationFromMiles = (miles) => Math.round((miles * 20) / 5) * 5; // 20 min/mile, rounded to nearest 5

  // Enforce tag consistency based on distance/difficulty
  const consistentTags = (miles, existingTags) => {
    const tags = new Set(existingTags || []);
    const diff = difficultyFromMiles(miles);

    // Difficulty & Pace: remove ALL conflicting, add correct ones
    ['easy', 'moderate', 'challenging', 'beginner-friendly', 'strenuous'].forEach(t => tags.delete(t));
    if (diff === 'Easy') { tags.add('easy'); tags.add('beginner-friendly'); }
    else if (diff === 'Moderate') { tags.add('moderate'); }
    else { tags.add('challenging'); if (miles > 7) tags.add('strenuous'); }

    // Duration: remove ALL conflicting, add correct one
    ['under-1-hour', '1-2-hours', 'half-day', 'full-day', 'epic'].forEach(t => tags.delete(t));
    if (miles < 2.5) tags.add('under-1-hour');
    else if (miles <= 5) tags.add('1-2-hours');
    else if (miles <= 10) tags.add('half-day');
    else tags.add('full-day');

    // Fitness & Intensity: remove ALL conflicting, add correct ones
    ['leisurely', 'fitness', 'high-mileage', 'power-walk'].forEach(t => tags.delete(t));
    if (miles < 3) tags.add('leisurely');
    else if (miles <= 5) tags.add('fitness');
    else { tags.add('high-mileage'); tags.add('fitness'); }

    return [...tags];
  };

  // Apply distance, difficulty, duration, and tags atomically in a single state update
  const applyDistanceDefaults = (miles, currentTags) => {
    const diff = difficultyFromMiles(miles);
    const dur = durationFromMiles(miles);
    const fixed = consistentTags(miles, currentTags || []);
    setFormData(prev => ({
      ...prev,
      difficulty: diff,
      duration_minutes: dur.toString(),
      tags: fixed,
    }));
    setDifficultyAutoAssigned(true);
  };
  const getTagWarnings = () => {
    const miles = parseFloat(formData.distance_miles);
    if (!miles || miles <= 0) return [];
    const tags = formData.tags || [];
    const warnings = [];
    if (miles >= 4 && tags.includes('under-1-hour')) warnings.push('"Under 1 Hour" on a ' + miles + '-mile walk');
    if (miles >= 5 && tags.includes('leisurely') && !tags.includes('high-mileage')) warnings.push('"Leisurely" without "High Mileage" on a ' + miles + '-mile walk');
    const diff = formData.difficulty;
    if (diff === 'Hard' && tags.includes('easy')) warnings.push('"Easy" tag on a Hard-difficulty walk');
    if (diff === 'Easy' && (tags.includes('challenging') || tags.includes('strenuous'))) warnings.push('"Challenging/Strenuous" tag on an Easy walk');
    return warnings;
  };
  const getDifficultyMismatch = () => {
    const miles = parseFloat(formData.distance_miles);
    if (!miles || miles <= 0) return null;
    const expected = difficultyFromMiles(miles);
    if (formData.difficulty && formData.difficulty !== expected) {
      return `${miles}-mile walk marked as ${formData.difficulty} — expected ${expected}`;
    }
    return null;
  };

  const fetchAiSuggestions = async (fieldKey, systemPrompt, itemLabel) => {
    setAiLoading(prev => ({ ...prev, [fieldKey]: true }));
    setAiSuggestions(prev => ({ ...prev, [fieldKey]: [] }));
    try {
      const vibes = formData.fun_categories?.vibe_in_3_words || [];
      const vibeStr = Array.isArray(vibes) ? vibes.join(', ') : vibes;
      const waypointNames = (formData.waypoints || []).map(wp => wp.name).filter(Boolean).join(', ');
      const userMessage = `Walk: ${formData.title}, Region: ${formData.region}, Categories: ${(formData.categories || []).join(', ')}, Vibe: ${vibeStr}, Description: ${formData.description}, Key stops: ${waypointNames}. Suggest 3 ${itemLabel} for this walk.`;

      const resp = await cmsFetch('/api/cms/ai-suggest', {
        method: 'POST',
        body: JSON.stringify({ system_prompt: systemPrompt, user_message: userMessage }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiSuggestions(prev => ({ ...prev, [fieldKey]: data.suggestions || [] }));
      } else {
        const err = await resp.text();
        console.error('AI suggest error:', err);
      }
    } catch (e) {
      console.error('AI suggest failed:', e);
    } finally {
      setAiLoading(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const suggestGearNotes = async () => {
    setAiLoading(prev => ({ ...prev, gear_notes: true }));
    try {
      const vibes = formData.fun_categories?.vibe_in_3_words || [];
      const vibeStr = Array.isArray(vibes) ? vibes.join(', ') : vibes;
      const userMessage = `Walk: ${formData.title || 'Untitled'}, ${formData.distance_meters ? (formData.distance_meters / 1609.34).toFixed(1) + ' miles' : 'unknown distance'}, ${formData.duration_minutes ? formData.duration_minutes + ' min' : 'unknown duration'}, ${formData.difficulty || 'moderate'} difficulty, in ${formData.neighborhood || 'unknown neighborhood'}, ${formData.city || formData.region || 'unknown city'}. Categories: ${(formData.categories || []).join(', ')}. Vibe: ${vibeStr}. Description: ${formData.description || ''}`;
      const systemPrompt = "Based on this walk, write a 2-3 sentence practical gear note for walkers. Be specific to this walk's characteristics — mention terrain, weather exposure, distance demands, or neighborhood context. Vary the writing style and tone so it doesn't sound templated. Don't start with 'I recommend' or 'You should'. Be conversational and opinionated. Return ONLY the gear note text, nothing else.";

      const resp = await cmsFetch('/api/cms/ai-suggest', {
        method: 'POST',
        body: JSON.stringify({ system_prompt: systemPrompt, user_message: userMessage }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const note = data.suggestions?.[0] || data.text || '';
        if (note) handleInputChange('gear_notes', note);
      }
    } catch (e) {
      console.error('Gear notes suggest failed:', e);
    } finally {
      setAiLoading(prev => ({ ...prev, gear_notes: false }));
    }
  };

  const suggestGear = async () => {
    setAiLoading(prev => ({ ...prev, gear_suggest: true }));
    try {
      const resp = await cmsFetch('/api/gear/suggest', {
        method: 'POST',
        body: JSON.stringify({
          categories: formData.categories || [],
          tags: formData.tags || [],
          walk_type_tags: formData.walk_type_tags || [],
          difficulty: formData.difficulty || 'Easy',
          distance_category: formData.distance_miles ? (
            parseFloat(formData.distance_miles) < 2 ? 'Short' :
            parseFloat(formData.distance_miles) <= 5 ? 'Medium' :
            parseFloat(formData.distance_miles) <= 10 ? 'Long' : 'Epic'
          ) : 'Medium',
          walk_shape: formData.walk_shape || 'loop',
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.suggestions?.length) {
          setRelatedGear(data.suggestions.map(g => ({
            slug: g.slug, name: g.name, category: g.category,
            brand: g.brand || '', price: g.price || 0,
          })));
        }
      }
    } catch (e) {
      console.error('Gear suggest failed:', e);
    } finally {
      setAiLoading(prev => ({ ...prev, gear_suggest: false }));
    }
  };

  // ── Master AI Generate ───────────────────────────────────────
  // Strip em dashes from all string values in AI output
  const cleanEmDashes = (obj) => {
    if (typeof obj === 'string') return obj.replace(/\s*[—–]\s*/g, ', ').replace(/,\s*,/g, ',');
    if (Array.isArray(obj)) return obj.map(cleanEmDashes);
    if (obj && typeof obj === 'object') {
      const cleaned = {};
      for (const [k, v] of Object.entries(obj)) cleaned[k] = cleanEmDashes(v);
      return cleaned;
    }
    return obj;
  };

  // Haversine distance in miles
  const haversine = (lat1, lng1, lat2, lng2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // #1 Truncate text at sentence boundary
  const truncateAtSentence = (text, maxLen) => {
    if (!text || text.length <= maxLen) return text;
    const truncated = text.slice(0, maxLen);
    const lastPeriod = truncated.lastIndexOf('.');
    return lastPeriod > maxLen * 0.5 ? truncated.slice(0, lastPeriod + 1) : truncated;
  };

  // Wait for Google Maps to load (up to 10 seconds)
  const waitForMaps = () => new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(true); return; }
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += 200;
      if (window.google?.maps?.places) { clearInterval(interval); resolve(true); }
      else if (elapsed >= 10000) { clearInterval(interval); resolve(false); }
    }, 200);
  });

  // #6 City-specific geocoding: always append city/state to query
  // Uses Places findPlaceFromQuery first, falls back to Geocoder
  const geocodeAddress = async (address, city) => {
    if (!address) return null;
    const mapsReady = await waitForMaps();

    const cityStr = city || formData.region || 'Los Angeles';
    const hasCity = address.toLowerCase().includes(cityStr.toLowerCase().split(',')[0]);
    const query = hasCity ? address : `${address}, ${cityStr}, CA`;

    // Try Places API first (returns rating, place_id, types)
    if (mapsReady && window.google?.maps?.places) {
      try {
        const placesResult = await new Promise((resolve) => {
          const el = document.createElement('div');
          const svc = new window.google.maps.places.PlacesService(el);
          svc.findPlaceFromQuery({ query, fields: ['geometry', 'place_id', 'name', 'formatted_address', 'rating', 'types'] }, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0]) {
              const p = results[0];
              const addr = p.formatted_address || '';
              const hasStreet = /\d/.test(addr.split(',')[0]) || p.types?.includes('street_address') || p.types?.includes('premise') || p.types?.includes('establishment');
              resolve({
                lat: p.geometry.location.lat(), lng: p.geometry.location.lng(),
                place_id: p.place_id, name: p.name, formatted_address: p.formatted_address,
                rating: p.rating || 0, types: p.types || [], streetLevel: hasStreet,
              });
            } else { resolve(null); }
          });
        });
        if (placesResult) return placesResult;
      } catch (e) { console.warn('Places geocode failed, trying Geocoder:', e); }
    }

    // Fallback: use Geocoder (always available if google.maps is loaded)
    if (window.google?.maps?.Geocoder) {
      try {
        return await new Promise((resolve) => {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: query }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              const r = results[0];
              const hasStreet = r.types?.includes('street_address') || r.types?.includes('premise') || r.types?.includes('establishment') || /\d/.test((r.formatted_address || '').split(',')[0]);
              resolve({
                lat: r.geometry.location.lat(), lng: r.geometry.location.lng(),
                place_id: r.place_id, name: query, formatted_address: r.formatted_address,
                rating: 0, types: r.types || [], streetLevel: hasStreet,
              });
            } else { resolve(null); }
          });
        });
      } catch (e) { console.warn('Geocoder fallback failed:', e); }
    }

    return null;
  };

  // Detect which sections already have content
  const getSectionStatus = () => {
    const has = (v) => v && (typeof v === 'string' ? v.trim() : Array.isArray(v) ? v.length > 0 : !!v);
    return {
      basic_info: has(formData.title) && has(formData.description),
      location: has(formData.starting_address) || (formData.waypoints || []).length > 0,
      content: selectedHighlights.length > 0 || has(formData.fun_categories?.theme_song),
      seo: has(formData.meta_title) || has(formData.seo_tags?.length),
    };
  };

  // Fetch a single section with timeout
  const fetchSection = (concept, section, extra = {}) => {
    return new Promise(async (resolve) => {
      const timer = setTimeout(() => resolve({ ok: false, error: 'Timed out after 45s' }), 45000);
      try {
        const resp = await cmsFetch('/api/cms/ai-generate-walk', {
          method: 'POST',
          body: JSON.stringify({ concept, section, ...extra }),
        });
        clearTimeout(timer);
        if (!resp.ok) { resolve({ ok: false, error: `API error ${resp.status}` }); return; }
        const raw = await resp.json();
        resolve({ ok: true, data: cleanEmDashes(raw.data) });
      } catch (e) {
        clearTimeout(timer);
        resolve({ ok: false, error: e.message });
      }
    });
  };

  // Apply a section's generated data to the form — only fill empty fields
  const applySection = async (section, data, city) => {
    // Set a field only if it's currently empty/unset
    const setIfEmpty = (field, value) => {
      const current = formData[field];
      const isEmpty = !current || current === '' || current === 0 ||
        (Array.isArray(current) && current.length === 0) ||
        (Array.isArray(current) && current.length === 1 && current[0] === '');
      if (isEmpty && value) handleInputChange(field, value);
    };

    if (section === 'basic_info') {
      setIfEmpty('title', data.title);
      setIfEmpty('description', data.description ? truncateAtSentence(data.description, 1200) : null);
      setIfEmpty('practical_summary', data.practical_summary ? truncateAtSentence(data.practical_summary, 300) : null);
      setIfEmpty('walk_shape', data.walk_shape);
      setIfEmpty('safety_level', data.safety_level);
      setIfEmpty('region', data.region);
      setIfEmpty('categories', data.categories);
      setIfEmpty('neighborhood', data.neighborhood);
      // Distance-based consistency: set difficulty, duration, and tags atomically
      // Use AI tags as base, then enforce distance rules on top
      const baseTags = (formData.tags?.length ? formData.tags : data.tags) || [];
      if (data.distance || formData.distance_miles) {
        const miles = parseFloat(data.distance || formData.distance_miles);
        if (!formData.distance_miles && data.distance) {
          handleInputChange('distance_meters', Math.round(miles * 1609.34));
          handleInputChange('distance_miles', miles.toFixed(1));
        }
        // This sets difficulty + duration + tags in ONE setFormData call
        applyDistanceDefaults(miles, baseTags);
      } else {
        // No distance available — use AI values as-is
        setIfEmpty('tags', data.tags);
        setIfEmpty('duration_minutes', data.duration);
        setIfEmpty('difficulty', data.difficulty);
      }
      if (data.difficulty || data.categories) {
        const cats = data.categories || [];
        const diff = (data.difficulty || '').toLowerCase();
        const isFlat = diff === 'easy' || cats.includes('family-friendly');
        const isNature = cats.includes('nature-parks') || cats.includes('scenic-walks');
        handleInputChange('accessibility', {
          ...formData.accessibility,
          wheelchair_accessible: isFlat, stroller_friendly: isFlat,
          dog_friendly: isNature || isFlat || cats.includes('scenic-walks'), parking_available: true,
          restrooms: isFlat || isNature,
          shade_coverage: isNature ? 'Good' : cats.includes('architecture') ? 'Limited' : 'Moderate',
          _ai_generated: true,
        });
      }
    } else if (section === 'location') {
      // Keyword-based type classification from name + description text
      const classifyByKeywords = (name, desc) => {
        const text = `${name} ${desc}`.toLowerCase();
        if (/restaurant|ristorante|trattoria|cafe|café|coffee|roaster|roastery|espresso|tea house|juice bar|bakery|bar\b|dive bar|tavern|taproom|speakeasy|brewery|eatery|dining|food|taco|pizza|deli|diner|bistro|pub\b|tapas|sushi|ramen|pho\b|gelato|ice cream|donut|bagel|brunch|winery|cocktail|lounge|cantina|gastropub|steak|hoagy|hoagie|burger|grill\b|sandwich|noodle/.test(text)) return 'food';
        if (/mural|street art|graffiti|art installation/.test(text)) return 'art';
        if (/museum/.test(text)) return 'museum';
        if (/gallery|galleries|exhibition|contemporary art|theater|theatre|venue|community center|cultural center/.test(text)) return 'cultural_venue';
        if (/church|cathedral|temple|synagogue|mosque|monument|library|city hall|historic/.test(text)) return 'architecture';
        if (/boutique|perfume|shop\b|store\b|retail|florist|salon|wine\b|wines\b|spirits|liquor|clothing|apparel|fashion|eyewear|glasses|optical|jewelry|accessories|bookstore|books\b|vintage|thrift|homewares|furniture|design store/.test(text)) return 'shop';
        return null;
      };

      // CRITICAL: Do not remove or simplify this type mapping.
      // Route stops default to "Point of Interest" without it.
      // Uses Google Places types array as primary source of truth.
      // Fixed 2026-04-15. Keyword fallback for when Places returns no useful type.
      const classifyStopByPlacesTypes = (types) => {
        if (!types || types.length === 0) return null;
        // Food & Drink
        if (types.some(t => ['restaurant', 'food', 'meal_takeaway', 'meal_delivery', 'cafe', 'bakery', 'bar', 'night_club', 'liquor_store'].includes(t))) return 'food';
        // Museum
        if (types.includes('museum')) return 'museum';
        // Cultural Venue (galleries)
        if (types.includes('art_gallery')) return 'cultural_venue';
        // Shop
        if (types.some(t => ['clothing_store', 'store', 'shopping_mall', 'department_store', 'shoe_store', 'jewelry_store', 'book_store', 'home_goods_store', 'furniture_store', 'electronics_store', 'bicycle_store', 'beauty_salon', 'hair_care', 'spa', 'pet_store'].includes(t)) || types.some(t => t.endsWith('_store'))) return 'shop';
        // Architecture
        if (types.some(t => ['church', 'place_of_worship', 'city_hall', 'library', 'courthouse', 'embassy'].includes(t))) return 'architecture';
        // Nature
        if (types.some(t => ['park', 'natural_feature', 'campground'].includes(t))) return 'viewpoint';
        // Lodging
        if (types.some(t => ['lodging', 'hotel'].includes(t))) return 'viewpoint';
        return null;
      };

      const classifyStop = (name, desc, geo, aiCategory) => {
        // 1. Google Places types are the primary source of truth
        const placesType = classifyStopByPlacesTypes(geo?.types);
        if (placesType) return placesType;
        // 2. Keyword fallback for when Places returned no useful business type
        const kwType = classifyByKeywords(name, desc);
        if (kwType) return kwType;
        // 3. AI-assigned category
        if (aiCategory && aiCategory !== 'viewpoint') return aiCategory;
        // 4. Default
        return 'viewpoint';
      };

      // Type-specific search hint for Places queries
      const typeSearchHint = { food: 'restaurant', art: 'mural', museum: 'museum', cultural_venue: 'venue', architecture: 'landmark' };

      // Check if a geocode result is within radius of an anchor point (miles)
      const isWithinRadius = (geo, anchor, maxMiles) => {
        if (!geo || !anchor) return true; // no anchor = can't validate, allow
        return haversine(geo.lat, geo.lng, anchor.lat, anchor.lng) <= maxMiles;
      };

      // Geocode start — store coords locally for Directions API + radius bounding
      let startCoords = null, endCoords = null;
      if (data.starting_address) {
        handleInputChange('starting_address', data.starting_address);
        handleInputChange('starting_point', data.starting_address);
        const startGeo = await geocodeAddress(data.starting_address, city);
        if (startGeo) {
          startCoords = { lat: startGeo.lat, lng: startGeo.lng };
          handleInputChange('starting_coords', startCoords);
        }
      }
      // For loop walks, end = start
      const isLoop = formData.walk_shape === 'loop' || data.walk_shape === 'loop';
      if (isLoop && startCoords && data.starting_address) {
        handleInputChange('ending_address', data.starting_address);
        handleInputChange('ending_point', data.starting_address);
        endCoords = { ...startCoords };
        handleInputChange('ending_coords', endCoords);
      } else if (data.ending_address) {
        handleInputChange('ending_address', data.ending_address);
        handleInputChange('ending_point', data.ending_address);
        const endGeo = await geocodeAddress(data.ending_address, city);
        if (endGeo) {
          endCoords = { lat: endGeo.lat, lng: endGeo.lng };
          handleInputChange('ending_coords', endCoords);
        }
      }

      // Fuzzy name similarity (0-1 score)
      const nameSimilarity = (a, b) => {
        if (!a || !b) return 0;
        const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const na = normalize(a), nb = normalize(b);
        if (na === nb) return 1;
        if (na.includes(nb) || nb.includes(na)) return 0.8;
        // Word overlap
        const wa = new Set(a.toLowerCase().split(/\s+/));
        const wb = new Set(b.toLowerCase().split(/\s+/));
        const overlap = [...wa].filter(w => wb.has(w) && w.length > 2).length;
        return overlap / Math.max(wa.size, wb.size);
      };

      // Geocode with type-aware retry + tight radius + hallucination guard
      const geocodeStop = async (address, name, desc, aiCategory, cityCtx) => {
        const category = classifyByKeywords(name, desc) || aiCategory;

        const validateGeo = (g) => {
          if (!g) return null;
          // Tight radius: must be within 2 miles of start
          if (!isWithinRadius(g, startCoords, 2)) return null;
          // Name similarity check
          if (g.name && name) {
            const sim = nameSimilarity(name, g.name);
            if (sim < 0.3) return null;
          }
          return g;
        };

        // Try 1: name + address combined (most specific query)
        let geo = null;
        if (name && address && name !== address) {
          geo = validateGeo(await geocodeAddress(`${name}, ${address}`, cityCtx));
        }
        // Try 2: full address alone
        if (!geo) {
          geo = validateGeo(await geocodeAddress(address, cityCtx));
        }
        // Try 3: name + type hint + city (for businesses)
        if ((!geo || (geo.rating === 0 && category === 'food')) && name) {
          const hint = typeSearchHint[category] || '';
          const hintQuery = hint ? `${name} ${hint}` : name;
          const geo2 = validateGeo(await geocodeAddress(hintQuery, cityCtx));
          if (geo2 && (geo2.rating > 0 || !geo)) geo = geo2;
        }
        // Try 4: just name + city
        if (!geo && name && name !== address) {
          geo = validateGeo(await geocodeAddress(name, cityCtx));
        }
        return geo;
      };

      if (data.waypoints) {
        // Step 1: Geocode all waypoints with type-aware search + classification
        const geocoded = await Promise.all(data.waypoints.map(async (wp, i) => {
          const geo = await geocodeStop(wp.address, wp.name, wp.description, wp.category, city);
          const category = classifyStop(wp.name, wp.description, geo, wp.category);
          // Flag low-confidence matches (rating 0 or very low name similarity)
          const lowConfidence = geo && geo.rating === 0 && geo.name && nameSimilarity(wp.name, geo.name) < 0.5;
          return {
            name: wp.name || '', address: geo?.formatted_address || wp.address || '',
            description: lowConfidence
              ? `${wp.description || ''}\n⚠️ Unverified location — verify this stop exists before publishing`
              : (wp.description || ''),
            order: i,
            coords: geo ? { lat: geo.lat, lng: geo.lng } : { lat: '', lng: '' },
            place_id: (geo && !lowConfidence) ? geo.place_id : null,
            user_ratings: geo?.rating || 0,
            category,
            types: geo?.types || [],
            _address_unresolved: !geo,
            _low_confidence: lowConfidence || false,
          };
        }));

        // Step 1b: Hard post-check — reject stops outside walk area entirely
        // Use median of resolved coords as anchor (more robust than mean against outliers)
        const resolvedCoords = geocoded
          .filter(wp => wp.coords.lat && wp.coords.lng)
          .map(wp => ({ lat: Number(wp.coords.lat), lng: Number(wp.coords.lng), name: wp.name }));

        let anchor = startCoords;
        if (resolvedCoords.length >= 3) {
          const sortedLat = [...resolvedCoords].sort((a, b) => a.lat - b.lat);
          const sortedLng = [...resolvedCoords].sort((a, b) => a.lng - b.lng);
          const mid = Math.floor(resolvedCoords.length / 2);
          anchor = { lat: sortedLat[mid].lat, lng: sortedLng[mid].lng };
        }

        // Log and reject outliers — REMOVE from array, don't keep
        const maxMilesFromAnchor = 1.5;
        const maxMilesFromStart = 2;
        const kept = [];
        for (const wp of geocoded) {
          if (!wp.coords.lat || !wp.coords.lng) {
            kept.push(wp); // Keep unresolved stops (they won't affect the map)
            continue;
          }
          const dAnchor = anchor ? haversine(Number(wp.coords.lat), Number(wp.coords.lng), anchor.lat, anchor.lng) : 0;
          const dStart = startCoords ? haversine(Number(wp.coords.lat), Number(wp.coords.lng), startCoords.lat, startCoords.lng) : 0;
          console.log(`[Route] Stop "${wp.name}": ${dAnchor.toFixed(2)}mi from anchor, ${dStart.toFixed(2)}mi from start, coords=(${Number(wp.coords.lat).toFixed(5)},${Number(wp.coords.lng).toFixed(5)})`);
          if (dAnchor > maxMilesFromAnchor || dStart > maxMilesFromStart) {
            console.warn(`[Route] REJECTED outlier "${wp.name}": ${dAnchor.toFixed(1)}mi from anchor, ${dStart.toFixed(1)}mi from start`);
            // Do NOT add to kept — stop is removed entirely
          } else {
            kept.push(wp);
          }
        }
        if (kept.length < 6) {
          console.warn(`[Route] Only ${kept.length} stops remaining after outlier rejection (need 6+)`);
        }

        // Replace geocoded with kept stops (max 10)
        const filtered = kept.slice(0, 10);

        // Step 2: Remove duplicates (within 100m)
        const deduped = [];
        for (const wp of filtered) {
          if (!wp.coords.lat || !wp.coords.lng) { deduped.push(wp); continue; }
          const isDupe = deduped.some(existing =>
            existing.coords.lat && existing.coords.lng &&
            haversine(Number(existing.coords.lat), Number(existing.coords.lng), Number(wp.coords.lat), Number(wp.coords.lng)) < 0.062
          );
          if (!isDupe) deduped.push(wp);
        }

        // Step 3: Use Google Directions API to optimize waypoint order
        const withCoords = deduped.filter(wp => wp.coords.lat && wp.coords.lng);
        const withoutCoords = deduped.filter(wp => !wp.coords.lat || !wp.coords.lng);
        let optimized = [...deduped];

        if (startCoords && withCoords.length >= 2 && window.google?.maps) {
          try {
            const origin = new window.google.maps.LatLng(startCoords.lat, startCoords.lng);
            // For loop walks, destination = origin to close the loop
            const isLoop = formData.walk_shape === 'loop' || data.walk_shape === 'loop';
            const dest = isLoop
              ? origin
              : (endCoords ? new window.google.maps.LatLng(endCoords.lat, endCoords.lng) : origin);
            // Cap at 8 intermediate waypoints (Google Directions API limit for optimization)
            const cappedWps = withCoords.slice(0, 8);
            const overflowWps = withCoords.slice(8);
            const dirWaypoints = cappedWps.map(wp => ({
              location: new window.google.maps.LatLng(Number(wp.coords.lat), Number(wp.coords.lng)),
              stopover: true,
            }));

            const directionsResult = await new Promise((resolve) => {
              const svc = new window.google.maps.DirectionsService();
              svc.route({
                origin, destination: dest,
                waypoints: dirWaypoints,
                travelMode: window.google.maps.TravelMode.WALKING,
                optimizeWaypoints: true,
              }, (result, status) => {
                if (status === 'OK') resolve(result);
                else { console.warn('Directions optimization failed:', status); resolve(null); }
              });
            });

            if (directionsResult) {
              const order = directionsResult.routes[0].waypoint_order;
              const reordered = order.map(idx => cappedWps[idx]);
              reordered.push(...overflowWps);
              reordered.push(...withoutCoords);
              reordered.forEach((wp, i) => { wp.order = i; });
              optimized = reordered;

              // Extract actual walking distance from Directions result
              const totalMeters = directionsResult.routes[0].legs.reduce((s, l) => s + l.distance.value, 0);
              const miles = parseFloat((totalMeters / 1609.34).toFixed(1));
              const diff = difficultyFromMiles(miles);
              const dur = durationFromMiles(miles);
              setFormData(prev => ({
                ...prev,
                distance_miles: miles.toString(),
                distance_meters: totalMeters,
                duration_minutes: dur.toString(),
                difficulty: diff,
                tags: consistentTags(miles, prev.tags),
              }));
              setDifficultyAutoAssigned(true);
            }
          } catch (e) {
            console.warn('Directions API optimization failed, using original order:', e);
          }
        }

        if (optimized === deduped) {
          optimized.forEach((wp, i) => { wp.order = i; });
        }

        handleInputChange('waypoints', optimized);
        setRouteWarnings([]);
      }
    } else if (section === 'content') {
      if (data.highlights) {
        const matched = await Promise.all(data.highlights.map(async (h, i) => {
          const geo = await geocodeAddress(h.title, city);
          if (geo && geo.rating >= 4.0) {
            const aiCat = h.category || '';
            // Infer category from Google types — expanded rules
            const t = geo.types || [];
            const inferredCat = (t.includes('restaurant') || t.includes('cafe') || t.includes('bar') || t.includes('bakery') || t.includes('food') || t.includes('meal_takeaway') || t.includes('meal_delivery')) ? 'food'
              : t.includes('museum') ? 'museum'
              : (t.includes('art_gallery') || geo.name?.toLowerCase().includes('mural') || geo.name?.toLowerCase().includes('art')) ? 'art'
              : (t.includes('park') || t.includes('natural_feature') || t.includes('campground')) ? 'viewpoint'
              : (t.includes('church') || t.includes('place_of_worship') || t.includes('city_hall') || t.includes('library')) ? 'architecture'
              : 'viewpoint'; // default to Point of Interest, not Hidden Gem
            return {
              id: geo.place_id || `cultural-${Date.now()}-${i}`, type: 'cultural',
              title: h.title, description: h.description || '', editorial_description: h.description || '',
              category: aiCat || inferredCat, user_ratings: geo.rating, source: 'google_places',
              formatted_address: geo.formatted_address, place_id: geo.place_id,
              icon: '📍', _auto_matched: true,
            };
          }
          if (geo && geo.rating > 0 && geo.rating < 4.0) {
            return {
              id: `regular-${Date.now()}-${i}`, type: 'regular',
              title: h.title, description: `${h.description || ''}\n⚠️ Low Google rating (${geo.rating.toFixed(1)}) — verify before publishing`,
              icon: '📍', _auto_matched: false, _low_rating: true,
            };
          }
          return { id: `regular-${Date.now()}-${i}`, type: 'regular', title: h.title, description: h.description || '', icon: '📍', _auto_matched: false };
        }));
        setSelectedHighlights(prev => [...prev, ...matched]);
      }
      const funCats = { ...(formData.fun_categories || {}) };
      if (data.theme_song && !funCats.theme_song) funCats.theme_song = data.theme_song;
      if (data.movie_that_matches && !funCats.movie_that_matches) funCats.movie_that_matches = data.movie_that_matches;
      if (data.instagram_moment && !funCats.instagram_moment) funCats.instagram_moment = data.instagram_moment;
      if (data.walking_challenge && !funCats.walking_challenge) funCats.walking_challenge = data.walking_challenge;
      if (data.perfect_snack && !funCats.perfect_snack) funCats.perfect_snack = data.perfect_snack;
      if (data.vibe_in_3_words && !funCats.vibe_in_3_words) funCats.vibe_in_3_words = data.vibe_in_3_words;
      handleInputChange('fun_categories', funCats);
      setIfEmpty('best_times', data.best_times);
      setIfEmpty('gear_notes', data.gear_notes);
    } else if (section === 'seo') {
      setIfEmpty('meta_title', data.meta_title);
      setIfEmpty('meta_description', data.meta_description);
      setIfEmpty('seo_tags', data.seo_tags);
    }
  };

  // Run generation for a list of sections
  const runGenerate = async (sectionsToRun) => {
    const concept = walkConcept.trim() || formData.title;
    if (!concept) return;
    masterAbort.current = false;
    setMasterGenerating(true);
    const labels = { basic_info: 'Basic Info', location: 'Location & Route', content: 'Content & Media', seo: 'Settings & SEO' };
    setMasterProgress({ current: sectionsToRun[0], completed: [], failed: [], total: sectionsToRun.length, labels });

    // Persist progress to sessionStorage so it survives refresh
    const storageKey = `wanderloop_gen_${formData.id || 'new'}`;
    sessionStorage.setItem(storageKey, JSON.stringify({ concept, sections: sectionsToRun, startedAt: Date.now() }));

    let city = formData.region || formData.city || 'los-angeles';
    // Capture walk shape before generation starts
    const walkShape = formData.walk_shape || 'loop';

    for (const section of sectionsToRun) {
      if (masterAbort.current) break;
      setMasterProgress(prev => ({ ...prev, current: section }));

      let result = await fetchSection(concept, section, { walk_shape: walkShape });

      // Retry location if waypoint count is short (< 8)
      if (result.ok && section === 'location') {
        const wpCount = result.data?.waypoints?.length || 0;
        if (wpCount < 8) {
          console.log(`Location generated ${wpCount} waypoints (need 8+), retrying...`);
          const retry = await fetchSection(concept, section, { walk_shape: walkShape });
          if (retry.ok && (retry.data?.waypoints?.length || 0) > wpCount) {
            result = retry;
          }
        }
      }

      if (result.ok) {
        if (section === 'basic_info' && result.data.region) city = result.data.region;
        await applySection(section, result.data, city);
        setMasterProgress(prev => ({ ...prev, completed: [...prev.completed, section] }));
      } else {
        console.error(`Generate ${section} failed:`, result.error);
        setMasterProgress(prev => ({ ...prev, failed: [...prev.failed, { section, error: result.error }] }));
      }
    }

    // FINAL PASS: enforce distance-based consistency on the LATEST state
    // This catches any cases where earlier setFormData calls were overwritten by stale closures
    setFormData(prev => {
      const miles = parseFloat(prev.distance_miles);
      if (!miles || miles <= 0) return prev;
      const diff = difficultyFromMiles(miles);
      const dur = durationFromMiles(miles);
      const fixed = consistentTags(miles, prev.tags);
      console.log(`[Auto-Generate] Final consistency pass: ${miles}mi → ${diff}, ${dur}min, tags: ${fixed.filter(t => ['easy','moderate','challenging','under-1-hour','1-2-hours','half-day','leisurely','fitness','high-mileage'].includes(t)).join(', ')}`);
      return { ...prev, difficulty: diff, duration_minutes: dur.toString(), tags: fixed };
    });
    setDifficultyAutoAssigned(true);

    // After all sections, fetch Unsplash images if none exist
    if (!masterAbort.current && !formData.main_image) {
      await fetchUnsplashImages(city);
    }

    // Auto-suggest gear if none selected
    if (!masterAbort.current && relatedGear.length === 0) {
      await suggestGear();
    }

    sessionStorage.removeItem(storageKey);
    setMasterGenerating(false);
  };

  // Fetch images from Unsplash + Wikimedia Commons
  const fetchUnsplashImages = async (cityOverride) => {
    const neighborhood = formData.region || formData.neighborhood || '';
    const city = cityOverride || formData.city || 'Los Angeles';
    const title = formData.title || '';

    // Fetch Unsplash and Wikimedia in parallel
    const [unsplashResult, wikimediaResult] = await Promise.allSettled([
      (async () => {
        const resp = await cmsFetch('/api/cms/unsplash-images', {
          method: 'POST',
          body: JSON.stringify({
            neighborhood, city,
            categories: formData.categories || [],
            count: 5,
            title,
            concept: walkConcept || title,
          }),
        });
        if (!resp.ok) return null;
        return resp.json();
      })(),
      (async () => {
        const resp = await cmsFetch('/api/cms/wikimedia-images', {
          method: 'POST',
          body: JSON.stringify({ neighborhood, city, count: 4, title }),
        });
        if (!resp.ok) return null;
        return resp.json();
      })(),
    ]);

    try {
      const unsplashData = unsplashResult.status === 'fulfilled' ? unsplashResult.value : null;
      const wikimediaData = wikimediaResult.status === 'fulfilled' ? wikimediaResult.value : null;

      const unsplashImages = unsplashData?.images || [];
      const wikimediaImages = wikimediaData?.images || [];

      // If Wikimedia returned few images, backfill from Unsplash
      const totalFetched = unsplashImages.length + wikimediaImages.length;
      let finalUnsplash = unsplashImages;
      if (wikimediaImages.length < 2 && unsplashImages.length < 8) {
        // Re-fetch more from Unsplash to fill 8 total
        try {
          const backfillResp = await cmsFetch('/api/cms/unsplash-images', {
            method: 'POST',
            body: JSON.stringify({
              neighborhood, city,
              categories: formData.categories || [],
              count: 8 - wikimediaImages.length,
              title,
              concept: walkConcept || title,
            }),
          });
          if (backfillResp.ok) {
            const backfill = await backfillResp.json();
            if (backfill.images?.length > unsplashImages.length) {
              finalUnsplash = backfill.images;
            }
          }
        } catch {}
      }

      // Build combined image + attribution arrays
      const fetchedImages = [];
      const fetchedAttrs = [];

      // Add Unsplash images
      finalUnsplash.forEach(img => {
        fetchedImages.push(img.url);
        const attr = {
          photographer: img.photographer,
          url: img.photographer_url,
          alt_text: img.alt_text || '',
          source: 'unsplash',
          download_link: img.download_link || '',
        };
        fetchedAttrs.push(attr);
        if (img.photographer) originalAttrMap.current.set(img.url, attr);
      });

      // Add Wikimedia images
      wikimediaImages.forEach(img => {
        fetchedImages.push(img.url);
        const attr = {
          photographer: img.photographer,
          url: img.photographer_url,
          alt_text: img.alt_text || '',
          source: 'wikimedia',
          license: img.license || '',
        };
        fetchedAttrs.push(attr);
        if (img.photographer) originalAttrMap.current.set(img.url, attr);
      });

      if (fetchedImages.length < 3) {
        console.warn(`Image fetch: only ${fetchedImages.length} images found (Unsplash: ${finalUnsplash.length}, Wikimedia: ${wikimediaImages.length})`);
        if (fetchedImages.length === 0) return;
      }

      // Collect existing manual images and their attributions (preserve them)
      const existingAttrs = formData._unsplash_attributions || [];
      const existingMain = formData.main_image || '';
      const existingImgs = formData.images || [];
      const allExisting = [existingMain, ...existingImgs].filter(Boolean);
      const manualImages = [];
      const manualAttrs = [];
      allExisting.forEach((url, i) => {
        const isManual = !url.includes('unsplash.com') && !url.includes('wikimedia.org') && !url.includes('upload.wikimedia');
        if (isManual) {
          manualImages.push(url);
          manualAttrs.push(existingAttrs[i] || { photographer: null, url: null, alt_text: '', source: 'manual' });
        }
      });

      // Merge: fetched images first (Unsplash + Wikimedia), then manual
      // Take up to 8 total
      const mergedUrls = [...fetchedImages, ...manualImages].slice(0, 10);
      const mergedAttrs = [...fetchedAttrs, ...manualAttrs].slice(0, 10);

      setFormData(prev => ({
        ...prev,
        main_image: mergedUrls[0],
        images: mergedUrls.slice(1),
        _unsplash_attributions: mergedAttrs,
      }));

      console.log(`[Images] ${finalUnsplash.length} Unsplash + ${wikimediaImages.length} Wikimedia + ${manualImages.length} manual = ${mergedUrls.length} total`);
    } catch (e) {
      console.warn('Image fetch processing failed:', e);
    }
  };

  const masterGenerate = () => runGenerate(['basic_info', 'location', 'content', 'seo']);

  const generateMissing = () => {
    const status = getSectionStatus();
    const missing = ['basic_info', 'location', 'content', 'seo'].filter(s => !status[s]);
    if (missing.length === 0) return;
    runGenerate(missing);
  };

  const retrySection = (section) => runGenerate([section]);

  // ── Recalculate Route ─────────────────────────────────────────
  const recalculateRoute = async () => {
    setRecalculating(true);
    setRecalcResult(null);
    const changes = [];

    try {
      await waitForMaps();
      const isLoop = formData.walk_shape === 'loop';

      // 1. Geocode starting address
      const startAddr = formData.starting_address || formData.starting_point;
      if (!startAddr) { setRecalcResult({ error: 'No starting address set' }); setRecalculating(false); return; }
      const startGeo = await geocodeAddress(startAddr, formData.region || formData.city);
      if (!startGeo) { setRecalcResult({ error: `Could not geocode starting address: "${startAddr}"` }); setRecalculating(false); return; }
      const oldStartLat = parseFloat(formData.starting_coords?.lat) || 0;
      handleInputChange('starting_coords', { lat: startGeo.lat, lng: startGeo.lng });
      handleInputChange('starting_point', startGeo.formatted_address || startAddr);
      if (oldStartLat && Math.abs(oldStartLat - startGeo.lat) > 0.0001) changes.push('Start coordinates updated');

      // 2. Geocode ending address (or copy from start for loops)
      if (isLoop) {
        handleInputChange('ending_coords', { lat: startGeo.lat, lng: startGeo.lng });
        handleInputChange('ending_point', startGeo.formatted_address || startAddr);
        handleInputChange('ending_address', startAddr);
      } else {
        const endAddr = formData.ending_address || formData.ending_point;
        if (endAddr) {
          const endGeo = await geocodeAddress(endAddr, formData.region || formData.city);
          if (endGeo) {
            handleInputChange('ending_coords', { lat: endGeo.lat, lng: endGeo.lng });
            handleInputChange('ending_point', endGeo.formatted_address || endAddr);
          }
        }
      }

      // 3. Re-geocode all waypoints
      const wps = formData.waypoints || [];
      if (wps.length > 0) {
        const regeocoded = await Promise.all(wps.map(async (wp, i) => {
          const geo = await geocodeAddress(wp.address || wp.name, formData.region || formData.city);
          return {
            ...wp,
            coords: geo ? { lat: geo.lat, lng: geo.lng } : wp.coords,
            place_id: geo?.place_id || wp.place_id,
            order: i,
          };
        }));

        // 4. Run Directions API to optimize order and get route
        if (window.google?.maps) {
          const origin = new window.google.maps.LatLng(startGeo.lat, startGeo.lng);
          const dest = isLoop ? origin : (() => {
            const ec = formData.ending_coords;
            return ec?.lat ? new window.google.maps.LatLng(parseFloat(ec.lat), parseFloat(ec.lng)) : origin;
          })();
          const withCoords = regeocoded.filter(wp => wp.coords?.lat && wp.coords?.lng);
          const withoutCoords = regeocoded.filter(wp => !wp.coords?.lat || !wp.coords?.lng);

          if (withCoords.length >= 2) {
            // Google Directions API: max 8 intermediate waypoints for optimization
            // If >8, use first 8 for the optimized route request
            const cappedWaypoints = withCoords.slice(0, 8);
            const overflowWaypoints = withCoords.slice(8);
            const dirWaypoints = cappedWaypoints.map(wp => ({
              location: new window.google.maps.LatLng(Number(wp.coords.lat), Number(wp.coords.lng)),
              stopover: true,
            }));
            if (withCoords.length > 8) {
              changes.push(`${withCoords.length} stops found, routing first 8 (API limit)`);
            }

            const result = await new Promise((resolve) => {
              const svc = new window.google.maps.DirectionsService();
              svc.route({
                origin, destination: dest,
                waypoints: dirWaypoints,
                travelMode: window.google.maps.TravelMode.WALKING,
                optimizeWaypoints: true,
              }, (res, status) => {
                if (status === 'OK') resolve(res);
                else { console.warn('Directions failed:', status); resolve(null); }
              });
            });

            if (result) {
              // Reorder stops based on optimized route
              const order = result.routes[0].waypoint_order;
              const reordered = order.map(idx => cappedWaypoints[idx]);
              // Append overflow + non-geocoded stops at the end
              reordered.push(...overflowWaypoints);
              reordered.push(...withoutCoords);
              reordered.forEach((wp, i) => { wp.order = i; });
              changes.push(`${reordered.length} stops reordered`);

              // Calculate distance and duration
              const totalMeters = result.routes[0].legs.reduce((s, l) => s + l.distance.value, 0);
              const totalSeconds = result.routes[0].legs.reduce((s, l) => s + l.duration.value, 0);
              const oldMiles = formData.distance_miles;
              const newMiles = (totalMeters / 1609.34).toFixed(1);
              const newMinutes = Math.round(totalSeconds / 60).toString();
              // Atomic update: distance, difficulty, duration, tags in one setState
              const milesNum = parseFloat(newMiles);
              const autoDiff = difficultyFromMiles(milesNum);
              const autoDur = durationFromMiles(milesNum);
              setFormData(prev => ({
                ...prev,
                distance_miles: newMiles,
                distance_meters: totalMeters,
                duration_minutes: autoDur.toString(),
                difficulty: autoDiff,
                tags: consistentTags(milesNum, prev.tags),
              }));
              setDifficultyAutoAssigned(true);
              if (oldMiles && oldMiles !== newMiles) changes.push(`Distance updated from ${oldMiles} to ${newMiles} mi`);
              else changes.push(`Distance: ${newMiles} mi, Duration: ${autoDur} min`);
              changes.push(`Difficulty auto-set to ${autoDiff}, tags updated`);

              handleInputChange('waypoints', reordered);
            } else {
              handleInputChange('waypoints', regeocoded);
              changes.push('Stops geocoded (Directions API unavailable)');
            }
          } else {
            handleInputChange('waypoints', regeocoded);
            changes.push('Stops geocoded');
          }
        } else {
          handleInputChange('waypoints', regeocoded);
          changes.push('Stops geocoded (Maps not loaded)');
        }
      }

      setRecalcResult({ success: true, changes });
    } catch (e) {
      setRecalcResult({ error: e.message });
    } finally {
      setRecalculating(false);
    }
  };

  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const startingAutocompleteRef = useRef(null);
  const endingAutocompleteRef = useRef(null);

  const handlePlaceSelect = (autocompleteRef, addressField, coordsField) => {
    if (!autocompleteRef.current) return;
    let place;
    try {
      place = autocompleteRef.current.getPlace();
    } catch {
      return;
    }
    if (!place || !place.geometry || !place.geometry.location) return;
    const { lat, lng } = place.geometry.location;
    handleInputChange(addressField, place.formatted_address);
    handleNestedInputChange(coordsField, 'lat', lat());
    handleNestedInputChange(coordsField, 'lng', lng());
    // Save the full formatted address (street, city, state, zip, country) for the point field
    const pointField = addressField === 'starting_address' ? 'starting_point' : 'ending_point';
    handleInputChange(pointField, place.formatted_address || place.name);
  };

  useEffect(() => {
    if (hasAccess && isEditing) {
      fetchWalk();
    }
  }, [hasAccess, id]);

  // Auto-calculate total walking distance via Directions API whenever
  // start/end coords or waypoints change.
  // Use a JSON fingerprint of waypoint coords so the effect doesn't re-fire
  // when unrelated formData changes create a new waypoints array reference.
  const waypointFingerprint = JSON.stringify(
    (formData.waypoints || [])
      .filter(wp => wp.coords && wp.coords.lat && wp.coords.lng)
      .map(wp => [wp.coords.lat, wp.coords.lng, wp.order])
  );

  useEffect(() => {
    if (!mapsLoaded) return;

    const sLat = parseFloat(formData.starting_coords.lat);
    const sLng = parseFloat(formData.starting_coords.lng);
    const eLat = parseFloat(formData.ending_coords.lat);
    const eLng = parseFloat(formData.ending_coords.lng);
    if (isNaN(sLat) || isNaN(sLng) || isNaN(eLat) || isNaN(eLng)) return;

    const origin = new window.google.maps.LatLng(sLat, sLng);
    const destination = new window.google.maps.LatLng(eLat, eLng);

    // Google Directions API allows max 25 waypoints, but optimize works best with ≤8
    const allWaypoints = formData.waypoints
      .filter(wp => wp.coords && wp.coords.lat && wp.coords.lng)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // If >8 waypoints, split into chunks and sum distances
    // Each Directions request can handle origin + dest + 8 intermediate = 10 points
    const MAX_WAYPOINTS = 8;
    if (allWaypoints.length <= MAX_WAYPOINTS) {
      const intermediateWaypoints = allWaypoints.map(wp => ({
        location: new window.google.maps.LatLng(parseFloat(wp.coords.lat), parseFloat(wp.coords.lng)),
        stopover: true,
      }));

      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin, destination,
          waypoints: intermediateWaypoints,
          travelMode: window.google.maps.TravelMode.WALKING,
          optimizeWaypoints: true,
        },
        (result, status) => {
          if (status === 'OK' && result.routes[0]) {
            const totalMeters = result.routes[0].legs.reduce((s, l) => s + l.distance.value, 0);
            const miles = parseFloat((totalMeters / 1609.34).toFixed(1));
            const diff = difficultyFromMiles(miles);
            const dur = durationFromMiles(miles);
            setFormData(prev => {
              if (prev.distance_miles === miles.toString()) return prev;
              return {
                ...prev,
                distance_miles: miles.toString(),
                distance_meters: totalMeters,
                duration_minutes: dur.toString(),
                difficulty: diff,
                tags: consistentTags(miles, prev.tags),
              };
            });
            setDifficultyAutoAssigned(true);
          }
        }
      );
    } else {
      // >8 waypoints: chain requests in segments
      const segments = [];
      for (let i = 0; i < allWaypoints.length; i += MAX_WAYPOINTS) {
        segments.push(allWaypoints.slice(i, i + MAX_WAYPOINTS));
      }

      let totalMeters = 0;
      let segOrigin = origin;

      const processSegments = async () => {
        const svc = new window.google.maps.DirectionsService();
        for (let s = 0; s < segments.length; s++) {
          const seg = segments[s];
          const segDest = s === segments.length - 1 ? destination
            : new window.google.maps.LatLng(parseFloat(seg[seg.length - 1].coords.lat), parseFloat(seg[seg.length - 1].coords.lng));
          const segWps = (s === segments.length - 1 ? seg : seg.slice(0, -1)).map(wp => ({
            location: new window.google.maps.LatLng(parseFloat(wp.coords.lat), parseFloat(wp.coords.lng)),
            stopover: true,
          }));

          const result = await new Promise(resolve => {
            svc.route({
              origin: segOrigin, destination: segDest,
              waypoints: segWps,
              travelMode: window.google.maps.TravelMode.WALKING,
              optimizeWaypoints: false, // preserve order within segment
            }, (res, status) => resolve(status === 'OK' ? res : null));
          });

          if (result) {
            totalMeters += result.routes[0].legs.reduce((s2, l) => s2 + l.distance.value, 0);
          }
          segOrigin = segDest;
        }

        const miles = parseFloat((totalMeters / 1609.34).toFixed(1));
        const diff = difficultyFromMiles(miles);
        const dur = durationFromMiles(miles);
        setFormData(prev => ({
          ...prev,
          distance_miles: miles.toString(),
          distance_meters: totalMeters,
          duration_minutes: dur.toString(),
          difficulty: diff,
          tags: consistentTags(miles, prev.tags),
        }));
        setDifficultyAutoAssigned(true);
      };
      processSegments().catch(e => console.warn('Segment distance calculation failed:', e));
    }
  }, [
    mapsLoaded,
    formData.starting_coords.lat,
    formData.starting_coords.lng,
    formData.ending_coords.lat,
    formData.ending_coords.lng,
    waypointFingerprint,
  ]);

  const fetchWalk = async () => {
    try {
      setLoading(true);
      const response = await cmsFetch(`/api/cms/walks/${id}`);

      if (response.ok) {
        const walk = await response.json();
        setFormData({
          ...walk,
          distance_miles: walk.distance_meters ? (walk.distance_meters / 1609.34).toFixed(1) : '',
          starting_address: walk.starting_point || '',
          ending_address: walk.ending_point || '',
          starting_coords: walk.starting_coords || { lat: '', lng: '' },
          ending_coords: walk.ending_coords || { lat: '', lng: '' },
          highlights: walk.highlights.length > 0 ? walk.highlights : [''],
          seo_tags: (walk.seo_tags || []).flatMap(t => t.includes(',') ? t.split(',').map(s => s.trim().toLowerCase().replace(/^#/, '')).filter(Boolean) : [t]),
          walk_type_tags: walk.walk_type_tags || [],
          cultural_pois: walk.cultural_pois || [],
          waypoints: walk.waypoints || [],
          best_times: Array.isArray(walk.best_times) ? walk.best_times : (walk.best_times ? [walk.best_times] : []),
          gear_notes: walk.gear_notes || '',
          _unsplash_attributions: walk._unsplash_attributions || [],
          fun_categories: {
            theme_song: '', vibe_in_3_words: '', instagram_moment: '',
            perfect_snack: '', walking_challenge: '', movie_that_matches: '',
            ...(walk.fun_categories || {}),
          },
          accessibility: {
            wheelchair_accessible: false,
            stroller_friendly: false,
            dog_friendly: false,
            parking_available: false,
            restrooms: false,
            shade_coverage: 'Limited',
            ...walk.accessibility
          }
        });
        const regularHighlights = (walk.highlights || []).filter(Boolean).map((h, i) => ({
          id: `regular-${i}`,
          type: 'regular',
          title: typeof h === 'string' ? h : (h.title || ''),
          description: typeof h === 'string' ? '' : (h.description || ''),
          icon: '📍',
        }));
        const culturalHighlights = (walk.cultural_pois || []).map((poi, i) => ({
          id: poi.place_id || `cultural-${i}`,
          type: 'cultural',
          title: poi.title || poi.name || String(poi),
          description: poi.formatted_address || poi.description || '',
          editorial_description: poi.editorial_description || '',
          category: poi.category || 'hidden_gem',
          subcategory: poi.subcategory || '',
          user_ratings: poi.user_ratings || poi.rating || 0,
          icon: poi.icon || '📍',
          source: poi.source || 'custom',
        }));
        setSelectedHighlights([...regularHighlights, ...culturalHighlights]);

        // Build immutable URL→attribution lookup from loaded data
        const loadedImgs = [walk.main_image, ...(walk.images || [])].filter(Boolean);
        const loadedAttrs = walk._unsplash_attributions || [];
        originalAttrMap.current = new Map();
        loadedImgs.forEach((url, i) => {
          if (loadedAttrs[i]?.photographer) {
            originalAttrMap.current.set(url, loadedAttrs[i]);
          }
        });

        // Hydrate linked gear items
        const gearIds = walk.related_gear_ids || [];
        if (gearIds.length > 0) {
          try {
            const gearRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gear`);
            if (gearRes.ok) {
              const gearData = await gearRes.json();
              const linked = (gearData.items || []).filter(g => gearIds.includes(g.slug));
              setRelatedGear(linked.map(g => ({ slug: g.slug, name: g.name, category: g.category, brand: g.brand })));
            }
          } catch (e) { console.error('Failed to load linked gear:', e); }
        }
      } else {
        console.error('Failed to fetch walk');
      }
    } catch (error) {
      console.error('Error fetching walk:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      const path = isEditing ? `/api/cms/walks/${id}` : `/api/cms/walks`;
      const method = isEditing ? 'PUT' : 'POST';

      const submitData = {
        title: formData.title || 'Untitled Walk',
        description: formData.description || 'No description provided',
        practical_summary: formData.practical_summary || '',
        starting_point: formData.starting_point || formData.starting_address || 'Unknown',
        ending_point: formData.ending_point || formData.ending_address || 'Unknown',
        starting_coords: {
          lat: parseFloat(formData.starting_coords.lat) || 34.0522,
          lng: parseFloat(formData.starting_coords.lng) || -118.2437,
        },
        ending_coords: {
          lat: parseFloat(formData.ending_coords.lat) || 34.0522,
          lng: parseFloat(formData.ending_coords.lng) || -118.2437,
        },
        route_coordinates: formData.waypoints
          .filter(wp => wp.coords && wp.coords.lat && wp.coords.lng)
          .map(wp => ({ lat: parseFloat(wp.coords.lat), lng: parseFloat(wp.coords.lng) })),
        waypoints: formData.waypoints
          .filter(wp => wp.address || wp.name)
          .map((wp, i) => ({
            name: wp.name || '',
            description: wp.description || '',
            address: wp.address || '',
            coords: {
              lat: parseFloat(wp.coords?.lat) || 0,
              lng: parseFloat(wp.coords?.lng) || 0,
            },
            place_id: wp.place_id || null,
            user_ratings: wp.user_ratings || 0,
            category: wp.category || 'viewpoint',
            order: i,
          })),
        distance_meters: Math.round((parseFloat(formData.distance_miles) || 1.0) * 1609.34),
        duration_minutes: parseInt(formData.duration_minutes) || 30,
        difficulty: formData.difficulty || 'Easy',
        walk_shape: formData.walk_shape || 'loop',
        safety_level: formData.safety_level || 'Safe Anytime',
        categories: Array.isArray(formData.categories) ? formData.categories : (formData.categories ? [formData.categories] : ['General']),
        region: formData.region || 'Los Angeles',
        country: formData.country || 'us',
        city: formData.city || 'los-angeles',
        neighborhood: formData.neighborhood || (formData.region ? formData.region.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '') : ''),
        seo_tags: Array.isArray(formData.seo_tags) ? formData.seo_tags : (formData.seo_tags ? [formData.seo_tags] : []),
        tags: formData.tags || [],
        walk_type_tags: formData.walk_type_tags || [],
        highlights: selectedHighlights.filter(h => h.type === 'regular').map(h => ({
          title: h.title,
          description: h.description || '',
        })),
        cultural_pois: selectedHighlights.filter(h => h.type === 'cultural').map(h => ({
          title: h.title,
          description: h.editorial_description || h.description || '',
          editorial_description: h.editorial_description || '',
          formatted_address: h.source === 'google_places' ? (h.description || '') : '',
          category: h.category || '',
          subcategory: h.subcategory || '',
          user_ratings: h.user_ratings || 0,
          source: h.source || 'google_places',
          place_id: h.id,
        })),
        accessibility: formData.accessibility || {},
        images: formData.images || [],
        main_image: formData.main_image || '',
        walk_type: formData.walk_type || 'curated',
        visibility: formData.visibility || 'public',
        status: formData.status || 'published',
        featured: Boolean(formData.featured),
        best_times: formData.best_times || [],
        gear_notes: formData.gear_notes || '',
        fun_categories: (() => {
          const fc = formData.fun_categories || {};
          const filled = Object.fromEntries(
            Object.entries(fc).filter(([, v]) => {
              if (Array.isArray(v)) return v.length > 0;
              return v && typeof v === 'string' && v.trim();
            })
          );
          return Object.keys(filled).length > 0 ? filled : null;
        })(),
        meta_title: formData.meta_title || '',
        meta_description: formData.meta_description || '',
        related_gear_ids: relatedGear.map(g => g.slug),
        // Rebuild attributions from immutable ref map — NEVER reads from formData._unsplash_attributions
        unsplash_attributions: [formData.main_image, ...(formData.images || [])].filter(Boolean).map(url => {
          const saved = originalAttrMap.current.get(url);
          if (saved?.photographer) return saved;
          return { photographer: null, url: null, alt_text: '', source: url.includes('unsplash.com') ? 'unsplash' : 'manual' };
        }),
      };

      const response = await cmsFetch(path, {
        method,
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Success! Created walk:', responseData);
        alert('Walk saved successfully!'); // Temporary feedback
        navigate('/cms/walks');
      } else {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          const detail = errorData.detail;
          let message;
          if (Array.isArray(detail)) {
            // Pydantic v2 validation errors: [{loc, msg, type, ...}]
            message = detail.map(e => {
              const field = Array.isArray(e.loc) ? e.loc.filter(p => p !== 'body').join('.') : '';
              return field ? `${field}: ${e.msg}` : e.msg;
            }).join(' | ');
          } else {
            message = typeof detail === 'string' ? detail : 'Failed to save walk';
          }
          setErrors({ general: message });
        } catch {
          setErrors({ general: `Server error: ${response.status} - ${errorText}` });
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      console.error('Error name:', error.name, 'Message:', error.message);
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        setErrors({ general: 'Could not reach the server. Check that the backend is running and try again.' });
      } else {
        setErrors({ general: `Error: ${error.message}` });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleArrayInputChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleTagInput = (value) => {
    // Handle comma-separated input properly
    if (value.includes(',')) {
      const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
      handleInputChange('seo_tags', tags);
    } else {
      // For single tags, store as array
      handleInputChange('seo_tags', value ? [value.trim()] : []);
    }
  };

  const handleCategoryInput = (value) => {
    // Handle comma-separated input properly  
    if (value.includes(',')) {
      const categories = value.split(',').map(cat => cat.trim()).filter(cat => cat);
      handleInputChange('categories', categories);
    } else {
      // For single categories, store as array
      handleInputChange('categories', value ? [value.trim()] : []);
    }
  };


  // Handle file drop for images
  const handleImageDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleImageUpload(files);
  };

  const handleImageUpload = async (files) => {
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const { url } = await uploadFile(file, 'walks');
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, url],
          main_image: prev.main_image || url,
          _unsplash_attributions: [...(prev._unsplash_attributions || []), { photographer: null, url: null, alt_text: file.name, source: 'manual' }],
        }));
      } catch (err) {
        console.error('Image upload failed:', file.name, err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading walk...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/cms/walks')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEditing ? 'Edit Walk' : 'Create New Walk'}
                </h1>
                <p className="text-sm text-gray-500">
                  {isEditing ? 'Update walk details and content' : 'Add a new walk to the content library'}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => navigate('/cms/walks')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {saving ? 'Saving...' : 'Save Walk'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
            <ul className="text-sm text-red-700 space-y-1">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>• {field}: {typeof error === 'string' ? error : JSON.stringify(error)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Master AI Generate */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-purple-900 mb-1.5">
                Walk Concept
              </label>
              <input
                type="text"
                value={walkConcept || formData.title || ''}
                onChange={(e) => setWalkConcept(e.target.value)}
                placeholder="Describe this walk in one line (e.g. 'Silver Lake reservoir loop with coffee stops and staircase streets')"
                className="w-full rounded-md border-purple-200 focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            <div className="flex flex-col gap-2 mt-6">
              <button
                type="button"
                onClick={masterGenerate}
                disabled={masterGenerating || !(walkConcept.trim() || formData.title)}
                className="px-5 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
              >
                {masterGenerating ? (
                  <><Loader className="w-4 h-4 animate-spin" /> Generating...</>
                ) : (
                  <><Star className="w-4 h-4" /> Auto-Generate All</>
                )}
              </button>
              {!masterGenerating && (
                <button
                  type="button"
                  onClick={generateMissing}
                  disabled={!(walkConcept.trim() || formData.title)}
                  className="px-5 py-2 bg-white text-purple-700 text-xs font-medium rounded-lg border border-purple-300 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-1.5"
                >
                  <Plus className="w-3 h-3" /> Generate Missing Fields
                </button>
              )}
            </div>
          </div>

          {/* Progress bar + section status */}
          {masterProgress && (
            <div className="mt-4">
              {masterGenerating && (
                <>
                  <div className="flex items-center justify-between text-xs text-purple-700 mb-1.5">
                    <span>Generating: {masterProgress.labels[masterProgress.current]}</span>
                    <span>{masterProgress.completed.length + (masterProgress.failed?.length || 0)} / {masterProgress.total}</span>
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-1.5">
                    <div
                      className="bg-purple-600 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${((masterProgress.completed.length + (masterProgress.failed?.length || 0)) / masterProgress.total) * 100}%` }}
                    />
                  </div>
                </>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {['basic_info', 'location', 'content', 'seo'].map(s => {
                  const isCompleted = masterProgress.completed.includes(s);
                  const failEntry = (masterProgress.failed || []).find(f => f.section === s);
                  const isCurrent = masterGenerating && masterProgress.current === s;
                  return (
                    <span
                      key={s}
                      className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                        isCompleted ? 'bg-green-600 text-white'
                          : failEntry ? 'bg-red-100 text-red-700'
                          : isCurrent ? 'bg-purple-200 text-purple-800 animate-pulse'
                          : 'bg-purple-100 text-purple-400'
                      }`}
                    >
                      {isCompleted && <span>✓</span>}
                      {failEntry && <span>✗</span>}
                      {masterProgress.labels[s]}
                      {failEntry && !masterGenerating && (
                        <button
                          type="button"
                          onClick={() => retrySection(s)}
                          className="ml-1 underline text-[10px] font-medium"
                        >
                          Retry
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'basic', name: 'Basic Info', icon: FileText },
              { id: 'location', name: 'Location & Route', icon: MapPin },
              { id: 'content', name: 'Content & Media', icon: ImageIcon },
              { id: 'settings', name: 'Settings & SEO', icon: Globe },
              ...(isEditing ? [{ id: 'feedback', name: 'Feedback', icon: Eye }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Walk Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter walk title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed description of the walk"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Practical Summary
                </label>
                <textarea
                  value={formData.practical_summary}
                  onChange={(e) => handleInputChange('practical_summary', e.target.value)}
                  rows={2}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief practical summary for quick overview"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distance (miles) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.distance_miles}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleInputChange('distance_miles', val);
                      const miles = parseFloat(val);
                      if (miles > 0) {
                        // Atomic update: keep tags/difficulty/duration in sync with new distance
                        setFormData(prev => ({
                          ...prev,
                          distance_miles: val,
                          distance_meters: Math.round(miles * 1609.34),
                          difficulty: difficultyFromMiles(miles),
                          duration_minutes: durationFromMiles(miles).toString(),
                          tags: consistentTags(miles, prev.tags),
                        }));
                        setDifficultyAutoAssigned(true);
                      }
                    }}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    placeholder="Auto-calculated from route"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.distance_miles
                      ? `≈ ${Math.round(parseFloat(formData.distance_miles) * 1609.34)}m · auto-calculated from route`
                      : 'Set start, end & waypoints to auto-calculate'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => handleInputChange('duration_minutes', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    placeholder="Auto-calculated from route"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.duration_minutes
                      ? `${formData.duration_minutes} min walking · auto-calculated`
                      : 'Set start, end & waypoints to auto-calculate'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty *
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => { handleInputChange('difficulty', e.target.value); setDifficultyAutoAssigned(false); }}
                    className={`w-full rounded-md focus:ring-blue-500 focus:border-blue-500 ${getDifficultyMismatch() ? 'border-amber-400' : 'border-gray-300'}`}
                    required
                  >
                    <option value="Easy">Easy</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Hard">Hard</option>
                  </select>
                  {difficultyAutoAssigned && (
                    <p className="text-xs text-blue-500 mt-1">Auto-assigned based on distance (can be manually adjusted)</p>
                  )}
                  {getDifficultyMismatch() && (
                    <p className="text-xs text-amber-600 mt-1">⚠ {getDifficultyMismatch()}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Walk Shape
                  </label>
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    {[
                      { value: 'loop', label: 'Loop', icon: '🔄' },
                      { value: 'out-and-back', label: 'Out & Back', icon: '↔️' },
                      { value: 'point-to-point', label: 'Point-to-Point', icon: '→' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={masterGenerating}
                        onClick={() => handleInputChange('walk_shape', opt.value)}
                        className={`flex-1 px-3 py-2 text-xs font-medium text-center transition-colors ${
                          formData.walk_shape === opt.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        } ${opt.value !== 'loop' ? 'border-l border-gray-300' : ''} ${masterGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {opt.icon} {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Safety */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Safety Level</label>
                  <select value={formData.safety_level} onChange={(e) => handleInputChange('safety_level', e.target.value)} className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                    <option value="Safe Anytime">Safe Anytime</option>
                    <option value="Safe Until Evening">Safe Until Evening</option>
                    <option value="Daytime Only">Daytime Only</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Region *
                  </label>
                  {formData.region === '__custom__' || (formData.region && ![
                    'Downtown LA','Silver Lake','Echo Park','Los Feliz','Hollywood',
                    'West Hollywood','Beverly Hills','Santa Monica','Venice','Culver City',
                    'Koreatown','Highland Park','Pasadena','Long Beach',
                    'New York','Chicago','San Francisco','Miami','London',
                  ].includes(formData.region)) ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.region === '__custom__' ? '' : formData.region}
                        onChange={(e) => handleInputChange('region', e.target.value)}
                        className="flex-1 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Type a region name..."
                        autoFocus
                        required
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange('region', '')}
                        className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md"
                      >
                        List
                      </button>
                    </div>
                  ) : (
                    <select
                      value={formData.region}
                      onChange={(e) => handleInputChange('region', e.target.value)}
                      className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select a region...</option>
                      <optgroup label="Los Angeles">
                        <option value="Downtown LA">Downtown LA</option>
                        <option value="Silver Lake">Silver Lake</option>
                        <option value="Echo Park">Echo Park</option>
                        <option value="Los Feliz">Los Feliz</option>
                        <option value="Hollywood">Hollywood</option>
                        <option value="West Hollywood">West Hollywood</option>
                        <option value="Beverly Hills">Beverly Hills</option>
                        <option value="Santa Monica">Santa Monica</option>
                        <option value="Venice">Venice</option>
                        <option value="Culver City">Culver City</option>
                        <option value="Koreatown">Koreatown</option>
                        <option value="Highland Park">Highland Park</option>
                        <option value="Pasadena">Pasadena</option>
                        <option value="Long Beach">Long Beach</option>
                      </optgroup>
                      <optgroup label="Other Cities">
                        <option value="New York">New York</option>
                        <option value="Chicago">Chicago</option>
                        <option value="San Francisco">San Francisco</option>
                        <option value="Miami">Miami</option>
                        <option value="London">London</option>
                      </optgroup>
                      <option value="__custom__">Other (type manually)...</option>
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Nature & Parks', slug: 'nature-parks' },
                      { label: 'Hidden Gems', slug: 'hidden-gems' },
                      { label: 'Architecture', slug: 'architecture' },
                      { label: 'Food-Focused Walks', slug: 'food-focused-walks' },
                      { label: 'Scenic Walks', slug: 'scenic-walks' },
                      { label: 'Historic Walks', slug: 'historic-walks' },
                      { label: 'Family-Friendly', slug: 'family-friendly' },
                      { label: 'Sunset Walks', slug: 'sunset-walks' },
                    ].map(cat => {
                      const selected = formData.categories.includes(cat.slug);
                      return (
                        <button
                          key={cat.slug}
                          type="button"
                          onClick={() => {
                            handleInputChange('categories',
                              selected
                                ? formData.categories.filter(c => c !== cat.slug)
                                : [...formData.categories, cat.slug]
                            );
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            selected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                          }`}
                        >
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Tags — functional descriptors for filtering & SEO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                  {formData.tags.length > 0 && <span className="text-gray-400 font-normal ml-1">({formData.tags.length} selected)</span>}
                </label>
                {TAG_GROUPS.map(({ group, tags }) => (
                  <div key={group} className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{group}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tag => {
                        const selected = (formData.tags || []).includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              const current = formData.tags || [];
                              handleInputChange('tags',
                                selected ? current.filter(t => t !== tag) : [...current, tag]
                              );
                            }}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              selected
                                ? 'bg-teal-600 text-white border-teal-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400 hover:text-teal-600'
                            }`}
                          >
                            {tagLabel(tag)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {getTagWarnings().length > 0 && (
                  <div className="mt-2 space-y-1">
                    {getTagWarnings().map((w, i) => (
                      <p key={i} className="text-xs text-amber-600">⚠ {w}</p>
                    ))}
                  </div>
                )}
              </div>

              {/* Geographic Taxonomy */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                  <select value={formData.country} onChange={(e) => handleInputChange('country', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                    <option value="us">United States</option>
                    <option value="uk">United Kingdom</option>
                    <option value="fr">France</option>
                    <option value="de">Germany</option>
                    <option value="jp">Japan</option>
                    <option value="au">Australia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <select value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                    <option value="los-angeles">Los Angeles</option>
                    <option value="new-york">New York</option>
                    <option value="chicago">Chicago</option>
                    <option value="san-francisco">San Francisco</option>
                    <option value="miami">Miami</option>
                    <option value="london">London</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Neighborhood</label>
                  {(() => {
                    const presets = ['downtown-la','silver-lake','echo-park','los-feliz','hollywood',
                      'west-hollywood','beverly-hills','santa-monica','venice','culver-city',
                      'koreatown','highland-park','pasadena','long-beach'];
                    const isPreset = presets.includes(formData.neighborhood);
                    const showInput = customNeighborhood || (formData.neighborhood && !isPreset);
                    return (
                      <>
                        <select
                          value={showInput ? '__custom__' : (formData.neighborhood || '')}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setCustomNeighborhood(true);
                              handleInputChange('neighborhood', '');
                            } else {
                              setCustomNeighborhood(false);
                              handleInputChange('neighborhood', e.target.value);
                            }
                          }}
                          className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Auto from Region</option>
                          {presets.map(n => (
                            <option key={n} value={n}>{n.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                          ))}
                          <option value="__custom__">Other (type manually)</option>
                        </select>
                        {showInput && (
                          <input
                            type="text"
                            value={formData.neighborhood || ''}
                            onChange={(e) => handleInputChange('neighborhood', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                            placeholder="e.g. arts-district"
                            className="w-full mt-2 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            autoFocus
                          />
                        )}
                      </>
                    );
                  })()}
                  <p className="text-xs text-gray-500 mt-1">Used in the walk URL path</p>
                </div>
              </div>
            </div>
          )}

          {/* Location & Route Tab */}
          {activeTab === 'location' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Starting Address *
                  </label>
                  {mapsLoaded ? (
                    <Autocomplete
                      onLoad={(ac) => { startingAutocompleteRef.current = ac; }}
                      onPlaceChanged={() => handlePlaceSelect(startingAutocompleteRef, 'starting_address', 'starting_coords')}
                      options={{ componentRestrictions: { country: 'us' } }}
                    >
                      <input
                        type="text"
                        defaultValue={formData.starting_address}
                        onChange={(e) => handleInputChange('starting_address', e.target.value)}
                        className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="1234 Hollywood Blvd, Los Angeles, CA"
                        required
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      value={formData.starting_address}
                      onChange={(e) => handleInputChange('starting_address', e.target.value)}
                      className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="1234 Hollywood Blvd, Los Angeles, CA"
                      required
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.starting_coords.lat
                      ? `📍 ${formData.starting_coords.lat}, ${formData.starting_coords.lng}`
                      : 'Select an address from the dropdown to populate coordinates'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ending Address *
                  </label>
                  {mapsLoaded ? (
                    <Autocomplete
                      onLoad={(ac) => { endingAutocompleteRef.current = ac; }}
                      onPlaceChanged={() => handlePlaceSelect(endingAutocompleteRef, 'ending_address', 'ending_coords')}
                      options={{ componentRestrictions: { country: 'us' } }}
                    >
                      <input
                        type="text"
                        defaultValue={formData.ending_address}
                        onChange={(e) => handleInputChange('ending_address', e.target.value)}
                        className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="5678 Sunset Blvd, Los Angeles, CA"
                        required
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      value={formData.ending_address}
                      onChange={(e) => handleInputChange('ending_address', e.target.value)}
                      className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="5678 Sunset Blvd, Los Angeles, CA"
                      required
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.ending_coords.lat
                      ? `📍 ${formData.ending_coords.lat}, ${formData.ending_coords.lng}`
                      : 'Leave same as start for loop walks'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Starting Point Address
                  </label>
                  <input
                    type="text"
                    value={formData.starting_point}
                    onChange={(e) => handleInputChange('starting_point', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="6801 Hollywood Blvd, Los Angeles, CA 90028, USA"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ending Point Address
                  </label>
                  <input
                    type="text"
                    value={formData.ending_point}
                    onChange={(e) => handleInputChange('ending_point', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Same as start for loop walks"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Starting Coordinates (Auto-populated)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      step="any"
                      value={formData.starting_coords.lat}
                      onChange={(e) => handleNestedInputChange('starting_coords', 'lat', e.target.value)}
                      className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                      placeholder="Latitude"
                      readOnly
                    />
                    <input
                      type="number"
                      step="any"
                      value={formData.starting_coords.lng}
                      onChange={(e) => handleNestedInputChange('starting_coords', 'lng', e.target.value)}
                      className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                      placeholder="Longitude"
                      readOnly
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Auto-populated from address</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ending Coordinates (Auto-populated)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      step="any"
                      value={formData.ending_coords.lat}
                      onChange={(e) => handleNestedInputChange('ending_coords', 'lat', e.target.value)}
                      className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                      placeholder="Latitude"
                      readOnly
                    />
                    <input
                      type="number"
                      step="any"
                      value={formData.ending_coords.lng}
                      onChange={(e) => handleNestedInputChange('ending_coords', 'lng', e.target.value)}
                      className="rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                      placeholder="Longitude"
                      readOnly
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Auto-populated from address</p>
                </div>
              </div>

              {/* Recalculate Route Button */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={recalculateRoute}
                  disabled={recalculating || !formData.starting_address}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {recalculating ? (
                    <><Loader className="w-4 h-4 animate-spin" /> Recalculating...</>
                  ) : (
                    <><MapPin className="w-4 h-4" /> Recalculate Route</>
                  )}
                </button>
                {recalcResult && (
                  recalcResult.error ? (
                    <span className="text-sm text-red-600">Error: {recalcResult.error}</span>
                  ) : (
                    <span className="text-sm text-green-700">
                      ✓ Route recalculated. {recalcResult.changes?.join('. ')}.
                    </span>
                  )
                )}
              </div>

              {routeWarnings.length > 0 && (
                <div className={`border rounded-lg p-3 mb-2 ${routeWarnings[0]?._autocorrected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  {routeWarnings[0]?._autocorrected ? (
                    <p className="text-xs font-semibold text-green-800">Route auto-corrected to fix geographic gaps. Please verify the new stops.</p>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-amber-800 mb-1.5">Route sequence warnings</p>
                      {routeWarnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-700">
                          Stop {w.from} ({w.fromName}) to Stop {w.to} ({w.toName}) is {w.distance} miles — verify route sequence
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Address resolution warnings */}
              {(formData.waypoints || []).some(wp => wp._address_unresolved) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                  <p className="text-xs font-semibold text-amber-800 mb-1.5">Address resolution warnings</p>
                  {formData.waypoints.filter(wp => wp._address_unresolved).map((wp, i) => (
                    <p key={i} className="text-xs text-amber-700">
                      Stop {wp.order + 1} ({wp.name || 'Unnamed'}) — address not resolved to street level. Enter a specific street address.
                    </p>
                  ))}
                </div>
              )}

              <WaypointEditor
                waypoints={formData.waypoints}
                onChange={(wps) => { handleInputChange('waypoints', wps); setRouteWarnings([]); }}
                mapsLoaded={mapsLoaded}
              />

              {/* Accessibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Accessibility Features
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: 'wheelchair_accessible', label: 'Wheelchair Accessible' },
                    { key: 'stroller_friendly', label: 'Stroller Friendly' },
                    { key: 'dog_friendly', label: 'Dog Friendly' },
                    { key: 'parking_available', label: 'Parking Available' },
                    { key: 'restrooms', label: 'Restrooms' }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.accessibility[key]}
                        onChange={(e) => handleNestedInputChange('accessibility', key, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shade Coverage
                  </label>
                  <select
                    value={formData.accessibility.shade_coverage}
                    onChange={(e) => handleNestedInputChange('accessibility', 'shade_coverage', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Limited">Limited</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Good">Good</option>
                    <option value="Excellent">Excellent</option>
                  </select>
                </div>
              </div>

              {/* Submission Source (read-only) */}
              {isEditing && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Submission Source
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                    {formData.walk_type === 'community' ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Community Submission
                          </span>
                        </div>
                        {formData.submitted_by ? (
                          <p className="text-gray-700">
                            Submitted by <span className="font-medium">{formData.submitted_by.name || 'Unknown'}</span>
                            {formData.submitted_by.email && (
                              <span className="text-gray-500"> ({formData.submitted_by.email})</span>
                            )}
                          </p>
                        ) : formData.source_user_id ? (
                          <p className="text-gray-500">User ID: {formData.source_user_id}</p>
                        ) : (
                          <p className="text-gray-500">Submitter info not available</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Admin Created
                        </span>
                        <span className="text-gray-500">Created directly via CMS</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content & Media Tab */}
          {activeTab === 'content' && (
            <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <HighlightsManager
                selectedHighlights={selectedHighlights}
                onHighlightsChange={setSelectedHighlights}
                walkCoordinates={
                  formData.starting_coords?.lat && formData.starting_coords?.lng
                    ? { lat: parseFloat(formData.starting_coords.lat), lng: parseFloat(formData.starting_coords.lng) }
                    : null
                }
                title="Walk Highlights & Cultural POIs"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Image
                </label>
                <div className="space-y-4">
                  {/* Image Upload Area */}
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors"
                    onDrop={handleImageDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-2">Drag and drop images here, or</p>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleImageUpload(Array.from(e.target.files))}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer"
                    >
                      Choose Images
                    </label>
                    <button
                      type="button"
                      onClick={() => fetchUnsplashImages(formData.region || formData.city)}
                      className="ml-3 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                    >
                      Fetch from Unsplash
                    </button>
                  </div>

                  {/* Unsplash attribution */}
                  {formData._unsplash_attributions?.[0] && (
                    <p className="text-xs text-gray-400">
                      Photo by{' '}
                      <a href={formData._unsplash_attributions[0].url} target="_blank" rel="noopener noreferrer" className="underline">
                        {formData._unsplash_attributions[0].photographer}
                      </a>{' '}
                      on Unsplash
                    </p>
                  )}

                  {/* URL Input Alternative */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Or paste image URLs (one per line or comma-separated):</label>
                    <textarea
                      id="image-url-textarea"
                      rows={3}
                      defaultValue=""
                      className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder={"https://example.com/image1.jpg\nhttps://example.com/image2.jpg"}
                    />
                    <div className="flex items-center gap-3 mt-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById('image-url-textarea');
                          const raw = textarea?.value || '';
                          const urls = raw.split(/[\n,]+/).map(u => u.trim()).filter(u => u.startsWith('http'));
                          if (urls.length === 0) return;
                          // Add manual attribution entries for each new URL
                          const newAttrs = urls.map(() => ({ photographer: null, url: null, alt_text: '', source: 'manual' }));
                          const existingAttrs = formData._unsplash_attributions || [];
                          if (!formData.main_image) {
                            handleInputChange('main_image', urls[0]);
                            if (urls.length > 1) {
                              handleInputChange('images', [...formData.images, ...urls.slice(1)]);
                            }
                          } else {
                            handleInputChange('images', [...formData.images, ...urls]);
                          }
                          handleInputChange('_unsplash_attributions', [...existingAttrs, ...newAttrs]);
                          textarea.value = '';
                        }}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Add URLs
                      </button>
                      <p className="text-xs text-gray-400">First URL becomes the main image if none is set. Others go to the gallery.</p>
                    </div>
                  </div>

                  {/* Unified draggable image gallery */}
                  {(() => {
                    // Merge main_image + images into one ordered list
                    const allImages = [
                      ...(formData.main_image ? [formData.main_image] : []),
                      ...formData.images.filter(img => img !== formData.main_image),
                    ];
                    if (allImages.length === 0) return null;

                    // Get attribution for a specific index (safe access)
                    const getAttr = (idx) => (formData._unsplash_attributions || [])[idx] || { photographer: null, url: null, alt_text: '', source: 'manual' };

                    // Build a full attributions array matching images for reorder operations
                    const buildFullAttrs = () => allImages.map((_, idx) => getAttr(idx));

                    const handleDragStart = (e, idx) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', idx.toString());
                      e.currentTarget.style.opacity = '0.5';
                    };
                    const handleDragEnd = (e) => { e.currentTarget.style.opacity = '1'; };
                    const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
                    const handleDropOnItem = (e, toIdx) => {
                      e.preventDefault();
                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (isNaN(fromIdx) || fromIdx === toIdx) return;
                      const reordered = [...allImages];
                      const fullAttrs = buildFullAttrs();
                      const [movedImg] = reordered.splice(fromIdx, 1);
                      const [movedAttr] = fullAttrs.splice(fromIdx, 1);
                      reordered.splice(toIdx, 0, movedImg);
                      fullAttrs.splice(toIdx, 0, movedAttr);
                      handleInputChange('main_image', reordered[0]);
                      handleInputChange('images', reordered.slice(1));
                      handleInputChange('_unsplash_attributions', fullAttrs);
                    };
                    const handleRemove = (idx) => {
                      const fullAttrs = buildFullAttrs();
                      const reordered = allImages.filter((_, i) => i !== idx);
                      const reorderedAttrs = fullAttrs.filter((_, i) => i !== idx);
                      handleInputChange('main_image', reordered[0] || '');
                      handleInputChange('images', reordered.slice(1));
                      handleInputChange('_unsplash_attributions', reorderedAttrs);
                    };

                    return (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Images ({allImages.length}) — drag to reorder, first image is the main image
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {allImages.map((image, idx) => (
                            <div
                              key={`${image}-${idx}`}
                              draggable
                              onDragStart={(e) => handleDragStart(e, idx)}
                              onDragEnd={handleDragEnd}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDropOnItem(e, idx)}
                              className={`relative group rounded-lg overflow-hidden cursor-grab active:cursor-grabbing ${
                                idx === 0 ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                              }`}
                            >
                              <img
                                src={image}
                                alt={idx === 0 ? 'Main image' : `Image ${idx + 1}`}
                                className="w-full h-24 object-cover cursor-pointer"
                                onClick={() => setImagePreviewIdx(idx)}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {/* Expand hint */}
                              <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                                <ZoomIn className="w-5 h-5 text-white drop-shadow" />
                              </div>
                              {/* Drag handle */}
                              <div className="absolute top-1 left-1 bg-black/40 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <GripVertical className="w-3.5 h-3.5 text-white" />
                              </div>
                              {/* Main badge */}
                              {idx === 0 && (
                                <span className="absolute bottom-1 left-1 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                  MAIN
                                </span>
                              )}
                              {/* Source badge */}
                              {(() => {
                                const attr = getAttr(idx);
                                const src = attr?.source || (image.includes('unsplash.com') ? 'unsplash' : (image.includes('wikimedia') ? 'wikimedia' : 'manual'));
                                const styles = {
                                  unsplash: 'bg-gray-700 text-white',
                                  wikimedia: 'bg-orange-600 text-white',
                                  manual: 'bg-blue-500 text-white',
                                  community_pending: 'bg-yellow-500 text-black',
                                  community_approved: 'bg-green-600 text-white',
                                };
                                const labels = { unsplash: 'Unsplash', wikimedia: 'Wikimedia', manual: 'Manual', community_pending: 'Pending', community_approved: 'Community' };
                                return (
                                  <span className={`absolute bottom-1 right-1 text-[8px] font-bold px-1 py-0.5 rounded ${styles[src] || styles.manual}`}>
                                    {labels[src] || 'Manual'}
                                  </span>
                                );
                              })()}
                              {/* Delete */}
                              <button
                                type="button"
                                onClick={() => handleRemove(idx)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Recommended Gear */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Recommended Gear</h3>
                  <p className="text-sm text-gray-500 mt-1">Link up to 5 gear items that pair well with this walk</p>
                </div>
                <button
                  type="button"
                  onClick={suggestGear}
                  disabled={aiLoading.gear_suggest}
                  className="px-3 py-1.5 text-xs font-medium text-purple-600 border border-purple-300 rounded-md hover:bg-purple-50 whitespace-nowrap flex items-center gap-1"
                >
                  {aiLoading.gear_suggest ? <Loader className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                  Suggest Gear
                </button>
              </div>

              {/* Selected gear chips */}
              {relatedGear.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {relatedGear.map(g => (
                    <span
                      key={g.slug}
                      className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {g.name}
                      <span className="text-blue-500 text-xs">({g.category})</span>
                      <button
                        type="button"
                        onClick={() => setRelatedGear(prev => prev.filter(x => x.slug !== g.slug))}
                        className="p-0.5 rounded-full hover:bg-blue-200 text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Gear picker — debounced server-side search */}
              {relatedGear.length < 5 && (
                <div className="relative">
                  <input
                    type="text"
                    value={gearSearch}
                    onChange={(e) => setGearSearch(e.target.value)}
                    onFocus={() => { clearTimeout(gearBlurTimeout.current); setGearDropdownOpen(true); }}
                    onBlur={() => { gearBlurTimeout.current = setTimeout(() => setGearDropdownOpen(false), 150); }}
                    placeholder="Type at least 2 characters to search gear..."
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {gearDropdownOpen && (() => {
                    const q = gearSearch.trim();
                    if (q.length < 2) return null;
                    const filtered = gearResults.filter(g => !relatedGear.some(rg => rg.slug === g.slug));
                    if (gearSearching && filtered.length === 0) {
                      return (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
                          Searching…
                        </div>
                      );
                    }
                    if (filtered.length === 0) {
                      return (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
                          No gear matches "{q}".
                        </div>
                      );
                    }
                    return (
                      <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg divide-y max-h-64 overflow-y-auto">
                        {filtered.map(g => (
                          <button
                            key={g.slug}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setRelatedGear(prev => [...prev, { slug: g.slug, name: g.name, category: g.category, brand: g.brand, price: g.price }]);
                              setGearSearch('');
                              setGearDropdownOpen(false);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center justify-between text-sm"
                          >
                            <div>
                              <span className="font-medium text-gray-900">{g.name}</span>
                              <span className="text-gray-500 ml-1.5">by {g.brand}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{g.category}</span>
                              <span className="text-xs font-medium text-gray-700">${g.price}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Walk Personality */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Walk Personality</h3>
                <p className="text-sm text-gray-500 mt-1">Optional fun metadata shown on the walk detail page</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Theme Song with AI suggest */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Theme Song</label>
                    <button
                      type="button"
                      disabled={aiLoading.theme_song}
                      onClick={() => fetchAiSuggestions('theme_song', THEME_SONG_SYSTEM_PROMPT, 'theme songs')}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50 flex items-center gap-1"
                    >
                      {aiLoading.theme_song ? <Loader className="w-3 h-3 animate-spin" /> : null}
                      Suggest →
                    </button>
                  </div>
                  <input
                    type="text"
                    value={formData.fun_categories?.theme_song || ''}
                    onChange={(e) => handleNestedInputChange('fun_categories', 'theme_song', e.target.value)}
                    placeholder='e.g., "Golden" by Jill Scott'
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {(aiSuggestions.theme_song || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {aiSuggestions.theme_song.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            handleNestedInputChange('fun_categories', 'theme_song', s);
                            setAiSuggestions(prev => ({ ...prev, theme_song: [] }));
                          }}
                          className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200 hover:bg-purple-100 transition-colors truncate max-w-full"
                          title={s}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Movie that Matches with AI suggest */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Movie that Matches</label>
                    <button
                      type="button"
                      disabled={aiLoading.movie_that_matches}
                      onClick={() => fetchAiSuggestions('movie_that_matches', MOVIE_SYSTEM_PROMPT, 'matching movies')}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50 flex items-center gap-1"
                    >
                      {aiLoading.movie_that_matches ? <Loader className="w-3 h-3 animate-spin" /> : null}
                      Suggest →
                    </button>
                  </div>
                  <input
                    type="text"
                    value={formData.fun_categories?.movie_that_matches || ''}
                    onChange={(e) => handleNestedInputChange('fun_categories', 'movie_that_matches', e.target.value)}
                    placeholder="e.g., Chinatown (1974)"
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {(aiSuggestions.movie_that_matches || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {aiSuggestions.movie_that_matches.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            handleNestedInputChange('fun_categories', 'movie_that_matches', s);
                            setAiSuggestions(prev => ({ ...prev, movie_that_matches: [] }));
                          }}
                          className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200 hover:bg-purple-100 transition-colors truncate max-w-full"
                          title={s}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Instagram Moment — plain text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instagram Moment</label>
                  <input
                    type="text"
                    value={formData.fun_categories?.instagram_moment || ''}
                    onChange={(e) => handleNestedInputChange('fun_categories', 'instagram_moment', e.target.value)}
                    placeholder="e.g., The mural at 2nd & Spring"
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                {/* Walking Challenge — dropdown with custom option */}
                {(() => {
                  const challengeOptions = [
                    'Spot 5 murals', 'Find a hidden staircase', 'Count the dogs',
                    'Try a local snack', 'Find street art by the same artist',
                    'Ask a local for their favorite spot', 'Take a photo that looks like a movie still',
                    "Find something that's been there 50+ years", 'Discover a hidden courtyard',
                  ];
                  const val = formData.fun_categories?.walking_challenge || '';
                  const isCustom = val && !challengeOptions.includes(val);
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Walking Challenge</label>
                      {isCustom ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={val}
                            onChange={(e) => handleNestedInputChange('fun_categories', 'walking_challenge', e.target.value)}
                            className="flex-1 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Type a custom challenge..."
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => handleNestedInputChange('fun_categories', 'walking_challenge', '')}
                            className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md"
                          >
                            List
                          </button>
                        </div>
                      ) : (
                        <select
                          value={val}
                          onChange={(e) => handleNestedInputChange('fun_categories', 'walking_challenge',
                            e.target.value === '__custom__' ? ' ' : e.target.value)}
                          className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        >
                          <option value="">Select a challenge...</option>
                          {challengeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          <option value="__custom__">Free text (custom)</option>
                        </select>
                      )}
                    </div>
                  );
                })()}

                {/* Perfect Snack — dropdown with custom option */}
                {(() => {
                  const snackOptions = [
                    'Iced coffee', 'Tacos', 'Elote', 'Boba', 'Açaí bowl',
                    'Soft pretzel', 'Paleta', 'Craft beer', 'Matcha latte',
                    'Breakfast burrito', 'Bacon egg and cheese', 'Juice cleanse (lol)',
                  ];
                  const val = formData.fun_categories?.perfect_snack || '';
                  const isCustom = val && !snackOptions.includes(val);
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Perfect Snack</label>
                      {isCustom ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={val}
                            onChange={(e) => handleNestedInputChange('fun_categories', 'perfect_snack', e.target.value)}
                            className="flex-1 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Type a custom snack..."
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => handleNestedInputChange('fun_categories', 'perfect_snack', '')}
                            className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md"
                          >
                            List
                          </button>
                        </div>
                      ) : (
                        <select
                          value={val}
                          onChange={(e) => handleNestedInputChange('fun_categories', 'perfect_snack',
                            e.target.value === '__custom__' ? ' ' : e.target.value)}
                          className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        >
                          <option value="">Select a snack...</option>
                          {snackOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          <option value="__custom__">Free text (custom)</option>
                        </select>
                      )}
                    </div>
                  );
                })()}

                {/* Vibe in 3 Words — chip selector, max 3 */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vibe in 3 Words
                    {(() => {
                      const vibes = formData.fun_categories?.vibe_in_3_words || [];
                      const arr = Array.isArray(vibes) ? vibes : (vibes ? vibes.split(',').map(s => s.trim()).filter(Boolean) : []);
                      return arr.length > 0 ? ` (${arr.length}/3)` : '';
                    })()}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['Gritty', 'Sunny', 'Moody', 'Buzzy', 'Quiet', 'Historic', 'Artsy', 'Foodie', 'Beachy', 'Industrial', 'Lush', 'Romantic', 'Edgy', 'Chill', 'Cinematic'].map(vibe => {
                      const raw = formData.fun_categories?.vibe_in_3_words || [];
                      const current = Array.isArray(raw) ? raw : (raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []);
                      const selected = current.includes(vibe);
                      return (
                        <button
                          key={vibe}
                          type="button"
                          onClick={() => {
                            let next;
                            if (selected) {
                              next = current.filter(v => v !== vibe);
                            } else if (current.length < 3) {
                              next = [...current, vibe];
                            } else {
                              return; // max 3
                            }
                            handleNestedInputChange('fun_categories', 'vibe_in_3_words', next);
                          }}
                          disabled={!selected && current.length >= 3}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-purple-600 text-white border-purple-600'
                              : current.length >= 3
                                ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400 hover:text-purple-600'
                          }`}
                        >
                          {vibe}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Best Times & Gear Notes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-900">Practical Notes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Best Times to Visit</label>
                  {[
                    { group: 'Time of Day', options: [
                      { key: 'early_morning', emoji: '🌅', label: 'Early Morning', desc: 'Before 9am — golden light, quiet streets, easy parking' },
                      { key: 'morning', emoji: '☀️', label: 'Morning', desc: '9am–noon — most businesses open, comfortable temps' },
                      { key: 'midday', emoji: '🌤', label: 'Midday', desc: 'Noon–3pm — peak energy, best for food stops' },
                      { key: 'late_afternoon', emoji: '🌇', label: 'Late Afternoon', desc: '3–6pm — cooling down, great light for photos' },
                      { key: 'sunset', emoji: '🌆', label: 'Sunset', desc: 'Golden hour — dramatic skies, lively atmosphere' },
                      { key: 'evening', emoji: '🌙', label: 'Evening', desc: 'After 7pm — cooler temps, restaurants in full swing' },
                    ]},
                    { group: 'Day of Week', options: [
                      { key: 'weekdays', emoji: '📅', label: 'Weekdays', desc: 'Mon–Fri — fewer crowds, faster service' },
                      { key: 'weekends', emoji: '🎉', label: 'Weekends', desc: 'Sat–Sun — markets open, livelier street scene' },
                    ]},
                    { group: 'Season & Conditions', options: [
                      { key: 'spring', emoji: '🌸', label: 'Spring', desc: 'Mar–May — wildflowers, mild temps, low tourist season' },
                      { key: 'summer', emoji: '🌊', label: 'Summer', desc: 'Jun–Aug — marine layer mornings, busy beaches and parks' },
                      { key: 'fall', emoji: '🍂', label: 'Fall', desc: 'Sep–Nov — best weather, Santa Ana winds possible' },
                      { key: 'winter', emoji: '🧥', label: 'Winter', desc: 'Dec–Feb — crisp and clear, mountains visible, thin crowds' },
                      { key: 'avoid_summer_midday', emoji: '⚠️', label: 'Avoid Summer Midday', desc: 'Exposed route — heat and sun make midday uncomfortable' },
                      { key: 'avoid_after_rain', emoji: '🌧️', label: 'Avoid After Rain', desc: 'Trail conditions poor — muddy or slippery for 1–2 days' },
                    ]},
                  ].map(({ group, options }) => (
                    <div key={group} className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{group}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {options.map(opt => {
                          const selected = (formData.best_times || []).includes(opt.key);
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => {
                                const current = formData.best_times || [];
                                handleInputChange('best_times',
                                  selected ? current.filter(k => k !== opt.key) : [...current, opt.key]
                                );
                              }}
                              title={opt.desc}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                selected
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              <span>{opt.emoji}</span> {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Gear Notes</label>
                    <button
                      type="button"
                      disabled={aiLoading.gear_notes}
                      onClick={suggestGearNotes}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50 flex items-center gap-1"
                    >
                      {aiLoading.gear_notes ? <Loader className="w-3 h-3 animate-spin" /> : null}
                      Suggest →
                    </button>
                  </div>
                  <textarea
                    value={formData.gear_notes}
                    onChange={(e) => handleInputChange('gear_notes', e.target.value)}
                    placeholder="e.g., Good walking shoes, sunscreen for exposed sections, a reusable water bottle."
                    rows={3}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>
            </>
          )}

          {/* Settings & SEO Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="pending_review">Pending Review</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Walk Type
                  </label>
                  <select
                    value={formData.walk_type}
                    onChange={(e) => handleInputChange('walk_type', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="curated">Curated</option>
                    <option value="community">Community</option>
                    <option value="gps_tracked">GPS Tracked</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visibility
                  </label>
                  <select
                    value={formData.visibility}
                    onChange={(e) => handleInputChange('visibility', e.target.value)}
                    className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="public">Public</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="private">Private</option>
                  </select>
                </div>
              </div>

              {/* Walk Type Tags — curator signal that drives gear matching */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Walk Type Tags
                  {(formData.walk_type_tags || []).length > 0 && (
                    <span className="text-gray-400 font-normal ml-1">({formData.walk_type_tags.length} selected)</span>
                  )}
                </label>
                <p className="text-xs text-gray-500 mb-2">Curator signal for gear matching. Pick any that apply.</p>
                <div className="flex flex-wrap gap-1.5">
                  {['beach','coastal','urban','hillside','food','nature','architecture','evening','loop','photography','historic','premium'].map(tag => {
                    const selected = (formData.walk_type_tags || []).includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          const current = formData.walk_type_tags || [];
                          handleInputChange('walk_type_tags',
                            selected ? current.filter(t => t !== tag) : [...current, tag]
                          );
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          selected
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                        }`}
                      >
                        {tag.charAt(0).toUpperCase() + tag.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                  />
                  <Star className="w-4 h-4 mr-1 text-yellow-500" />
                  Featured Walk
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Tags
                </label>
                {/* Tag chips */}
                <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                  {formData.seo_tags.map((tag, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleInputChange('seo_tags', formData.seo_tags.filter((_, idx) => idx !== i))}
                        className="p-0.5 rounded-full hover:bg-blue-200 text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {/* Add tag input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Type a tag and press Enter, or paste comma-separated tags..."
                    className="flex-1 rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const raw = e.target.value;
                        const newTags = raw.split(',')
                          .map(t => t.trim().toLowerCase().replace(/^#/, ''))
                          .filter(t => t && !formData.seo_tags.includes(t));
                        if (newTags.length) {
                          handleInputChange('seo_tags', [...formData.seo_tags, ...newTags]);
                        }
                        e.target.value = '';
                      }
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData('text');
                      if (pasted.includes(',')) {
                        e.preventDefault();
                        const newTags = pasted.split(',')
                          .map(t => t.trim().toLowerCase().replace(/^#/, ''))
                          .filter(t => t && !formData.seo_tags.includes(t));
                        if (newTags.length) {
                          handleInputChange('seo_tags', [...formData.seo_tags, ...newTags]);
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const region = (formData.region || '').toLowerCase();
                      const title = (formData.title || '').toLowerCase();
                      const cats = formData.categories || [];
                      const suggested = new Set(formData.seo_tags);

                      // Region-based
                      if (region) {
                        suggested.add(`${region} walks`);
                        suggested.add(region);
                      }
                      // Category-based
                      cats.forEach(cat => {
                        const c = cat.toLowerCase();
                        suggested.add(`${c} walk`);
                        if (region) suggested.add(`${c} ${region}`);
                      });
                      // Title words (3+ chars, skip common words)
                      const skip = new Set(['the','and','a','an','to','of','in','at','for','on','walk','from','with']);
                      title.split(/\s+/).forEach(w => {
                        const clean = w.replace(/[^a-z0-9]/g, '');
                        if (clean.length >= 3 && !skip.has(clean)) suggested.add(clean);
                      });
                      // Generic
                      suggested.add('walking tour');
                      suggested.add('things to do');
                      if (region) suggested.add(`things to do in ${region}`);

                      handleInputChange('seo_tags', [...suggested]);
                    }}
                    className="px-3 py-2 text-xs font-medium text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 whitespace-nowrap"
                  >
                    Auto-suggest tags
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.seo_tags.length} tag{formData.seo_tags.length !== 1 ? 's' : ''} · press Enter to add · paste comma-separated · click × to remove
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Meta Title</label>
                  <button
                    type="button"
                    onClick={() => {
                      const title = formData.title || 'Walk';
                      const region = formData.region || 'Los Angeles';
                      handleInputChange('meta_title', `${title} in ${region}, Los Angeles | Wanderloop`);
                    }}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    Auto-generate →
                  </button>
                </div>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => handleInputChange('meta_title', e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="SEO optimized title"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Meta Description</label>
                  <button
                    type="button"
                    disabled={aiLoading.meta_description}
                    onClick={async () => {
                      setAiLoading(prev => ({ ...prev, meta_description: true }));
                      try {
                        const waypointNames = (formData.waypoints || []).map(wp => wp.name).filter(Boolean).join(', ');
                        const userMessage = `Walk title: ${formData.title}, Region: ${formData.region}, City: Los Angeles, Categories: ${(formData.categories || []).join(', ')}, Description: ${formData.description}, Key stops: ${waypointNames}. Write the meta description.`;
                        const resp = await cmsFetch('/api/cms/ai-suggest', {
                          method: 'POST',
                          body: JSON.stringify({ system_prompt: META_DESCRIPTION_SYSTEM_PROMPT, user_message: userMessage }),
                        });
                        if (resp.ok) {
                          const data = await resp.json();
                          const desc = (data.suggestions || []).join(' ').trim();
                          if (desc) handleInputChange('meta_description', desc);
                        }
                      } catch (e) {
                        console.error('Meta description generation failed:', e);
                      } finally {
                        setAiLoading(prev => ({ ...prev, meta_description: false }));
                      }
                    }}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50 flex items-center gap-1"
                  >
                    {aiLoading.meta_description ? <Loader className="w-3 h-3 animate-spin" /> : null}
                    Auto-generate →
                  </button>
                </div>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => handleInputChange('meta_description', e.target.value)}
                  rows={3}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="SEO meta description (150-160 characters)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.meta_description ? `${formData.meta_description.length} characters` : ''}
                </p>
              </div>
            </div>
          )}

          {/* Feedback Tab */}
          {activeTab === 'feedback' && isEditing && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Community Feedback</h3>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const resp = await cmsFetch(`/api/cms/walks/${id}/feedback`);
                      if (resp.ok) {
                        const data = await resp.json();
                        setWalkFeedback(data.feedback || []);
                      }
                    } catch (e) { console.error('Failed to load feedback:', e); }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Refresh
                </button>
              </div>

              {walkFeedback.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">No feedback submitted yet.</p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const resp = await cmsFetch(`/api/cms/walks/${id}/feedback`);
                        if (resp.ok) { const data = await resp.json(); setWalkFeedback(data.feedback || []); }
                      } catch {}
                    }}
                    className="text-xs text-blue-600 mt-2 hover:underline"
                  >
                    Load feedback
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {walkFeedback.map((fb) => (
                    <div key={fb.id} className={`border rounded-lg p-4 ${
                      fb.status === 'applied' ? 'border-green-200 bg-green-50/50' :
                      fb.status === 'reviewed' ? 'border-blue-200 bg-blue-50/50' :
                      'border-gray-200'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              {fb.category === 'route' ? 'Route' : fb.category === 'stop' ? 'Stop' : fb.category === 'description' ? 'Description' : fb.category === 'missing' ? 'Missing Spot' : 'Other'}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              fb.status === 'applied' ? 'bg-green-100 text-green-700' :
                              fb.status === 'reviewed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {fb.status}
                            </span>
                            <span className="text-xs text-gray-400">
                              {fb.created_at ? new Date(fb.created_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-800">{fb.message}</p>
                          {fb.email && <p className="text-xs text-gray-400 mt-1">{fb.email}</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {fb.status !== 'reviewed' && (
                            <button
                              type="button"
                              onClick={async () => {
                                await cmsFetch(`/api/cms/feedback/${fb.id}`, {
                                  method: 'PUT', body: JSON.stringify({ status: 'reviewed' }),
                                });
                                setWalkFeedback(prev => prev.map(f => f.id === fb.id ? { ...f, status: 'reviewed' } : f));
                              }}
                              className="text-xs px-2 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50"
                            >
                              Reviewed
                            </button>
                          )}
                          {fb.status !== 'applied' && (
                            <button
                              type="button"
                              onClick={async () => {
                                await cmsFetch(`/api/cms/feedback/${fb.id}`, {
                                  method: 'PUT', body: JSON.stringify({ status: 'applied' }),
                                });
                                setWalkFeedback(prev => prev.map(f => f.id === fb.id ? { ...f, status: 'applied' } : f));
                              }}
                              className="text-xs px-2 py-1 border border-green-300 text-green-600 rounded hover:bg-green-50"
                            >
                              Applied
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>
      </div>

      {/* Image Preview Lightbox */}
      {imagePreviewIdx !== null && (() => {
        const allImages = [
          ...(formData.main_image ? [formData.main_image] : []),
          ...formData.images.filter(img => img !== formData.main_image),
        ];
        if (allImages.length === 0 || imagePreviewIdx >= allImages.length) return null;
        const image = allImages[imagePreviewIdx];
        const attr = (formData._unsplash_attributions || [])[imagePreviewIdx] || {};
        const source = attr?.source || (image.includes('unsplash.com') ? 'unsplash' : 'manual');
        const hasPrev = imagePreviewIdx > 0;
        const hasNext = imagePreviewIdx < allImages.length - 1;
        const goPrev = () => hasPrev && setImagePreviewIdx(imagePreviewIdx - 1);
        const goNext = () => hasNext && setImagePreviewIdx(imagePreviewIdx + 1);

        return (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
            onClick={(e) => { if (e.target === e.currentTarget) setImagePreviewIdx(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setImagePreviewIdx(null);
              if (e.key === 'ArrowLeft') goPrev();
              if (e.key === 'ArrowRight') goNext();
            }}
            tabIndex={0}
            ref={(el) => el?.focus()}
          >
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
              <div className="flex items-center gap-3">
                <span className="text-white/80 text-sm font-medium">
                  Image {imagePreviewIdx + 1} of {allImages.length}
                </span>
                {imagePreviewIdx === 0 && (
                  <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">MAIN</span>
                )}
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                  source === 'unsplash' ? 'bg-gray-700 text-white' : source === 'wikimedia' ? 'bg-orange-600 text-white' : 'bg-blue-500 text-white'
                }`}>
                  {source === 'unsplash' ? 'Unsplash' : source === 'wikimedia' ? 'Wikimedia' : 'Manual'}
                </span>
              </div>
              <button
                onClick={() => setImagePreviewIdx(null)}
                className="text-white/70 hover:text-white p-1 rounded transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Previous arrow */}
            {hasPrev && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <img
              src={image}
              alt={`Image ${imagePreviewIdx + 1}`}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />

            {/* Next arrow */}
            {hasNext && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Footer metadata */}
            <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/60 to-transparent">
              <div className="flex items-end justify-between">
                <div className="text-white/80 text-sm space-y-0.5">
                  {attr?.photographer && (
                    <p>
                      {source === 'wikimedia' ? 'Image' : 'Photo'} by{' '}
                      <a href={attr.url} target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-blue-300">
                        {attr.photographer}
                      </a>
                      {source === 'unsplash' && ' on Unsplash'}
                      {source === 'wikimedia' && ' via Wikimedia Commons'}
                      {source === 'wikimedia' && attr.license && (
                        <span className="text-white/50 ml-1">({attr.license})</span>
                      )}
                    </p>
                  )}
                  {!attr?.photographer && source === 'manual' && (
                    <p className="text-white/50 italic">Manual upload — no attribution</p>
                  )}
                </div>
                {/* Thumbnail strip */}
                <div className="flex gap-1.5 max-w-[50vw] overflow-x-auto">
                  {allImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setImagePreviewIdx(i); }}
                      className={`flex-shrink-0 w-10 h-10 rounded overflow-hidden border-2 transition-all ${
                        i === imagePreviewIdx ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-90'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CMSWalkForm;